import React,{useEffect,useMemo,useRef,useState} from "react";
import {createRoot} from "react-dom/client";
import * as echarts from "echarts";
import {ReactFlow,Background,Controls,MiniMap,MarkerType} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {Command} from "cmdk";
import {
  Activity,BrainCircuit,Building2,BookOpen,CheckCircle2,ChevronRight,CircleDollarSign,
  Command as CommandIcon,CreditCard,Database,GitBranch,Globe2,KeyRound,Layers3,
  LockKeyhole,LogIn,Network,Orbit,PlugZap,Search,ShieldCheck,Sparkles,Target,
  Users,Waypoints,Workflow,X,Zap
} from "lucide-react";
import "./styles.css";
import HandoverGuide from "./HandoverGuide.jsx";

const NAV=[
  ["command","Executive Deck",Orbit],
  ["ceo","CEO Operating Room",BrainCircuit],
  ["organism","Company Organism",Network],
  ["strategy","Strategy Lab",Layers3],
  ["decisions","Decisions",GitBranch],
  ["departments","Departments",Building2],
  ["cognition","Cognition",BrainCircuit],
  ["authority","Authority",ShieldCheck],
  ["integrations","Integrations",Waypoints],
];

const cls=(...xs)=>xs.filter(Boolean).join(" ");
const num=(v,d=1)=>v==null?"—":Number(v).toLocaleString(undefined,{maximumFractionDigits:d});
const pct=(v,d=0)=>v==null?"—":(Number(v)*100).toFixed(d)+"%";

function useChart(option,deps=[]){
  const ref=useRef(null);
  useEffect(()=>{
    if(!ref.current)return;
    const chart=echarts.init(ref.current,null,{renderer:"canvas"});
    chart.setOption(option,true);
    const resize=()=>chart.resize();
    addEventListener("resize",resize);
    return()=>{removeEventListener("resize",resize);chart.dispose();};
  },deps);
  return ref;
}

function Panel({title,kicker,action,className,children}){
  return <section className={cls("panel",className)}>
    <header className="panel-head">
      <div>{kicker&&<div className="kicker">{kicker}</div>}<h2>{title}</h2></div>
      {action&&<div className="panel-action">{action}</div>}
    </header>
    {children}
  </section>;
}

function Kpi({label,value,note,tone="neutral"}){
  return <div className={"kpi tone-"+tone}>
    <div className="kpi-label">{label}</div>
    <div className="kpi-value">{value}</div>
    <div className="kpi-note">{note}</div>
  </div>;
}

function Pill({children,tone="neutral"}){
  return <span className={"pill "+tone}>{children}</span>;
}

function StatusDot({tone="green"}){
  return <span className={"status-dot "+tone}/>;
}

function Sparkline({values=[]}){
  const option=useMemo(()=>({
    animationDuration:450,
    grid:{left:0,right:0,top:5,bottom:0},
    xAxis:{type:"category",show:false,data:values.map((_,i)=>i)},
    yAxis:{type:"value",show:false},
    series:[{type:"line",smooth:.35,symbol:"none",data:values,
      lineStyle:{width:2,color:"#68e0ff"},
      areaStyle:{color:"rgba(104,224,255,.08)"}}],
  }),[values]);
  const ref=useChart(option,[option]);
  return <div className="spark" ref={ref}/>;
}

function AutonomyChart({families}){
  const counts={};
  Object.values(families||{}).forEach(v=>counts[v]=(counts[v]||0)+1);
  const palette={
    autonomous:"#55dfad",
    autonomous_with_notice:"#68e0ff",
    approval_required:"#f2b75d",
    prohibited:"#ff627b",
  };
  const option=useMemo(()=>({
    tooltip:{trigger:"item",backgroundColor:"#0b111d",borderColor:"#25334d",textStyle:{color:"#eaf0fb"}},
    legend:{bottom:0,textStyle:{color:"#73819a",fontSize:10}},
    series:[{type:"pie",radius:["57%","79%"],center:["50%","43%"],
      label:{show:false},padAngle:3,itemStyle:{borderRadius:6},
      data:Object.entries(counts).map(([name,value])=>({name,value,itemStyle:{color:palette[name]||"#77859d"}}))}],
  }),[families]);
  const ref=useChart(option,[option]);
  return <div className="donut" ref={ref}/>;
}

function OutcomeWeightsChart({learning}){
  const roles=Object.entries(learning?.roles||{});
  const option=useMemo(()=>({
    grid:{left:74,right:18,top:10,bottom:24},
    xAxis:{type:"value",min:.5,max:1.9,axisLabel:{color:"#718097"},splitLine:{lineStyle:{color:"rgba(255,255,255,.05)"}}},
    yAxis:{type:"category",data:roles.map(([r])=>r.toUpperCase()),axisLabel:{color:"#9ba9bd",fontWeight:600},axisLine:{show:false},axisTick:{show:false}},
    series:[{type:"bar",barWidth:16,data:roles.map(([,s])=>s.weight),
      itemStyle:{color:"#8d7cff",borderRadius:[0,5,5,0]},
      markLine:{silent:true,symbol:"none",lineStyle:{color:"#68e0ff",type:"dashed"},data:[{xAxis:1}]}}],
  }),[learning]);
  const ref=useChart(option,[option]);
  return <div className="chart short" ref={ref}/>;
}


function OntologyGraph({graph}){
  const kindStyle={
    company:{background:"#101b2c",border:"1px solid rgba(104,224,255,.35)",color:"#e9f5ff"},
    executive:{background:"#15152c",border:"1px solid rgba(141,124,255,.4)",color:"#eee9ff"},
    entity_type:{background:"#0d1722",border:"1px solid rgba(255,255,255,.12)",color:"#cbd7e8"},
    entity:{background:"#091019",border:"1px solid rgba(255,255,255,.08)",color:"#8f9cb0"},
  };
  const basePositions=[
    [420,20],[420,155],[120,320],[390,320],[660,320],[120,470],[390,470],[660,470]
  ];
  const nodes=(graph.nodes||[]).map((node,i)=>({
    id:node.id,data:{label:node.label},position:{x:basePositions[i]?.[0]??(i%4)*230,y:basePositions[i]?.[1]??(620+Math.floor(i/4)*120)},
    style:{width:175,borderRadius:13,padding:11,fontSize:11,textAlign:"center",...(kindStyle[node.kind]||kindStyle.entity)}
  }));
  const edges=(graph.edges||[]).map((edge,i)=>({
    id:"oe"+i,source:edge.source,target:edge.target,
    label:edge.kind,markerEnd:{type:MarkerType.ArrowClosed,color:"#52627c"},
    style:{stroke:"#465773",strokeWidth:1.2},labelStyle:{fill:"#617087",fontSize:8}
  }));
  return <div className="flow-area">
    <ReactFlow nodes={nodes} edges={edges} fitView fitViewOptions={{padding:.25}}
      nodesDraggable={false} nodesConnectable={false} proOptions={{hideAttribution:true}}>
      <Background color="#1b283e" gap={26} size={1}/>
      <Controls showInteractive={false}/>
      <MiniMap nodeColor={()=>"#344967"} maskColor="rgba(3,6,11,.72)"/>
    </ReactFlow>
  </div>;
}

function LoopGraph({mission}){
  const nodes=(mission.nodes||[]).map((label,i)=>{
    const angle=(i/(mission.nodes.length))*Math.PI*2-Math.PI/2;
    return {id:String(i),data:{label},position:{x:390+Math.cos(angle)*300,y:260+Math.sin(angle)*205},
      style:{width:150,padding:10,borderRadius:12,textAlign:"center",fontSize:10,
        background:i===8?"#10201d":"#0c1421",color:i===8?"#baffdf":"#ced8e8",
        border:i===8?"1px solid rgba(85,223,173,.3)":"1px solid rgba(255,255,255,.09)"}};
  });
  const edges=(mission.edges||[]).map(([a,b],i)=>({
    id:"me"+i,source:String(a),target:String(b),animated:i===mission.edges.length-1,
    markerEnd:{type:MarkerType.ArrowClosed,color:i===mission.edges.length-1?"#55dfad":"#56647d"},
    style:{stroke:i===mission.edges.length-1?"#55dfad":"#485771",strokeWidth:i===mission.edges.length-1?2:1.2}
  }));
  return <div className="loop-area"><ReactFlow nodes={nodes} edges={edges} fitView
    fitViewOptions={{padding:.12}} nodesDraggable={false} nodesConnectable={false}
    proOptions={{hideAttribution:true}}><Background color="#1c293d" gap={28} size={1}/></ReactFlow></div>;
}

function ExecutiveDeck({data,setSection}){
  const world=data.brief?.world||{};
  const decisions=data.report?.decisions||{};
  const pending=data.approvals?.pending||[];
  const dissent=data.dissent?.open_dissent||[];
  const plan=data.plans?.plans?.[0];
  const activity=(data.activity?.recent_events||[]).map((_,i)=>i+1);
  const morning=data.morning_brief||{};
  return <div className="page-stack">
    <section className="hero">
      <div className="hero-copy">
        <div className="eyebrow"><Sparkles size={13}/> Autonomous executive organism</div>
        <h1>See the company. See ARC thinking. See what happens next.</h1>
        <p>{data.company.name} is represented as a governed world model, executive board,
          digital-twin decision system, authority envelope and outcome-learning loop.</p>
        <div className="hero-actions">
          <button className="primary" onClick={()=>setSection("organism")}>Enter organism <ChevronRight size={15}/></button>
          <button className="ghost" onClick={()=>setSection("cognition")}>Inspect cognition</button>
        </div>
      </div>
      <div className="hero-core">
        <div className="core-orb"><BrainCircuit size={32}/></div>
        <span className="ring r1"/><span className="ring r2"/><span className="ring r3"/>
        <div className="core-caption"><b>ARC executive loop</b><span>observe → decide → learn</span></div>
      </div>
    </section>

    <div className="kpi-grid">
      <Kpi label="Recorded facts" value={num(world.fact_count,0)} note={data.runtime?"tenant world model · provenance preserved":"sandbox world model"} tone="cyan"/>
      <Kpi label="Decision closure" value={(decisions.with_outcome||0)+"/"+(decisions.total||0)} note="outcomes recorded" tone="green"/>
      <Kpi label="Awaiting owner" value={pending.length} note="approval queue" tone={pending.length?"amber":"green"}/>
      <Kpi label="Open dissent" value={dissent.length} note="executive board challenges" tone={dissent.length?"amber":"green"}/>
      <Kpi label="Autonomy preset" value={data.authority?.preset||"—"} note="customer authority envelope" tone="violet"/>
      <Kpi label="Test surface" value={num(data.test_cases_discovered,0)} note="discovered test cases" tone="cyan"/>
    </div>

    <Panel title="Executive Morning Brief" kicker={(morning.date||"today")+" · owner attention budget"}>
      <div className="morning-deck">
        <div className="morning-headline">
          <div><span>{data.runtime?"ARC synthesized from this tenant's recorded state":"ARC synthesized from recorded demo state"}</span><h2>{morning.headline||"Morning brief is not available yet."}</h2></div>
          <div className="morning-budget"><b>{morning.attention_budget?.shown||0}</b><span>needs you</span><small>{morning.attention_budget?.suppressed||0} lower-priority queued</small></div>
        </div>
        <div className="morning-columns">
          <div className="morning-column">
            <span className="morning-label">Since yesterday</span>
            <b>{num(morning.since_yesterday?.completed_actions,0)} completed actions</b>
            <small>{num(morning.since_yesterday?.decisions_with_outcome,0)}/{num(morning.since_yesterday?.decisions_total,0)} decisions measured</small>
            {(morning.since_yesterday?.items||[]).slice(0,3).map((item,i)=><div className="morning-row" key={i}><i/><span>{item.title}</span></div>)}
          </div>
          <div className="morning-column owner">
            <span className="morning-label">Needs you</span>
            {(morning.owner_attention||[]).length
              ?(morning.owner_attention||[]).slice(0,4).map((item,i)=><div className="morning-attention" key={i}><ShieldCheck size={13}/><div><b>{item.title}</b><span>{item.detail}</span></div></div>)
              :<div className="morning-clear"><CheckCircle2 size={14}/><span>No reserved owner decision.</span></div>}
          </div>
          <div className="morning-column">
            <span className="morning-label">ARC today</span>
            {(morning.today||[]).slice(0,4).map((item,i)=><div className="morning-task" key={i}><Target size={13}/><div><b>{item.title}</b><span>{item.detail}</span></div></div>)}
            {!(morning.today||[]).length&&<small>No recorded action plan.</small>}
          </div>
          <div className="morning-column">
            <span className="morning-label">What ARC learned</span>
            {(morning.learning||[]).slice(0,3).map((item,i)=><div className="morning-task" key={i}><BrainCircuit size={13}/><div><b>{item.title}</b><span>{item.detail}</span></div></div>)}
            <div className="morning-truth"><Globe2 size={12}/><span>Observed/recorded state only; simulations must remain labelled.</span></div>
          </div>
        </div>
      </div>
    </Panel>

    <div className="two-col wide-left">
      <Panel title="Executive operating loop" kicker="Living organism · closed feedback">
        <LoopGraph mission={data.mission_graph}/>
      </Panel>
      <Panel title="What requires attention" kicker="Decision surface">
        <div className="attention-list">
          {pending.map(x=><div className="attention amber" key={x.intent_id}><ShieldCheck size={15}/><div><b>Owner approval</b><span>{x.action_type} · {x.risk} risk</span></div></div>)}
          {dissent.map((x,i)=><div className="attention violet" key={i}><GitBranch size={15}/><div><b>{x.severity} dissent</b><span>{x.question} · {x.challenger}</span></div></div>)}
          {(data.obligations?.obligations||[]).map(x=><div className="attention cyan" key={x.obligation_id}><Target size={15}/><div><b>{x.kind.replaceAll("_"," ")}</b><span>{x.detail}</span></div></div>)}
        </div>
        {plan&&<div className="plan-block">
          <div className="plan-head"><span>Active mission</span><b>{Math.round(plan.progress*100)}%</b></div>
          <h3>{plan.goal}</h3>
          <div className="progress"><i style={{width:(plan.progress*100)+"%"}}/></div>
          <div className="milestones">{plan.milestones.map(m=><span className={m.done?"done":""} key={m.name}>{m.done?"✓":"○"} {m.name}</span>)}</div>
        </div>}
      </Panel>
    </div>

    <div className="two-col">
      <Panel title="Activity pulse" kicker="Ledger-backed operational trace">
        <Sparkline values={activity}/>
        <div className="event-list">{(data.activity?.recent_events||[]).slice(-7).reverse().map(e=>
          <div className="event" key={e.seq}><span className="seq">{String(e.seq).padStart(2,"0")}</span><b>{e.type.replaceAll("."," ")}</b><small>{e.intent_id||"ledger event"}</small></div>)}</div>
      </Panel>
      <Panel title="Company reality" kicker="Observed facts only">
        <div className="entity-grid">{Object.entries(world.by_entity||{}).map(([name,count])=>
          <div className="entity-card" key={name}><Database size={16}/><b>{name}</b><span>{count} observed</span></div>)}</div>
        <div className="truth-banner"><Globe2 size={16}/><div><b>{data.evidence_class}</b><span>{data.truth_note}</span></div></div>
      </Panel>
    </div>
  </div>;
}


function Organism({data}){
  return <div className="page-stack">
    <section className="section-hero">
      <div><div className="eyebrow"><Network size={13}/> Company organism</div>
      <h1>The ontology is ARC's operational reality.</h1>
      <p>Facts, entities, authority and executive state are connected rather than rendered as disconnected SaaS tables.</p></div>
      <Pill tone="cyan">observed reality only</Pill>
    </section>
    <Panel title="Company ontology" kicker="Nouns, relationships, executive control">
      <OntologyGraph graph={data.ontology}/>
    </Panel>
    <div className="two-col">
      <Panel title="Self-model" kicker="What ARC knows about itself">
        <div className="self-grid">
          <Kpi label="Know" value={num(data.self_model?.knowledge?.know,0)} note="observed facts" tone="cyan"/>
          <Kpi label="Believe" value={num(data.self_model?.knowledge?.believe,0)} note="inferred beliefs" tone="violet"/>
          <Kpi label="Project" value={num(data.self_model?.knowledge?.project,0)} note="forecasts" tone="amber"/>
        </div>
        <div className="callout">
          <BrainCircuit size={17}/><div><b>Operational self-awareness</b><span>
          ARC tracks uncertainty, disagreements, prediction errors, open loops and authority — engineering state, not a consciousness claim.</span></div>
        </div>
      </Panel>
      <Panel title="Learning state" kicker="Outcome & correction feedback">
        <div className="metric-lines">
          <div><span>Autonomous coverage</span><b>{pct(data.learning?.coverage?.autonomous_rate)}</b></div>
          <div><span>Escalation rate</span><b>{pct(data.learning?.coverage?.escalation_rate)}</b></div>
          <div><span>Correction rate</span><b>{pct(data.learning?.coverage?.correction_rate)}</b></div>
          <div><span>Prediction errors retained</span><b>{num(data.self_model?.prediction_errors?.count,0)}</b></div>
          <div><span>Open learning loops</span><b>{num(data.self_model?.open_loops?.count,0)}</b></div>
        </div>
      </Panel>
    </div>
  </div>;
}

function Decisions({data}){
  const pending=data.approvals?.pending||[];
  const dissent=data.dissent?.open_dissent||[];
  const entries=data.self_model?.prediction_errors?.recent_errors||[];
  return <div className="page-stack">
    <section className="section-hero"><div>
      <div className="eyebrow"><GitBranch size={13}/> Decision intelligence</div>
      <h1>Every important choice should leave a lineage.</h1>
      <p>Evidence → independent views → challenge → simulation → authority → action → observed outcome → error analysis.</p>
    </div><Pill tone={pending.length?"amber":"green"}>{pending.length} owner gate</Pill></section>

    <div className="decision-lane">
      {["Evidence","Independent views","Challenge","Digital Twin","Authority","Execution","Outcome","Learning"].map((x,i)=>
        <div className={cls("decision-node",i===6&&"active")} key={x}><span>{String(i+1).padStart(2,"0")}</span><b>{x}</b></div>)}
    </div>

    <div className="two-col">
      <Panel title="Open executive dissent" kicker="Disagreement is preserved">
        {dissent.length?dissent.map((x,i)=><div className="dissent-card" key={i}>
          <div><Pill tone="amber">{x.severity}</Pill><b>{x.question}</b></div>
          <p>{x.challenger.toUpperCase()} challenges {x.role.toUpperCase()}: {x.objection}</p>
        </div>):<div className="empty">No open dissent.</div>}
      </Panel>
      <Panel title="Outcome learning" kicker="Errors become executive memory">
        {entries.length?entries.map(x=><div className="error-card" key={x.entry_id}>
          <b>{x.chosen_action}</b><span>{x.outcome}</span><p>{x.error_analysis}</p>
        </div>):<div className="empty">No resolved prediction errors.</div>}
      </Panel>
    </div>

    <Panel title="Pending owner decisions" kicker="Constitutional authority remains explicit">
      <div className="table-wrap"><table><thead><tr><th>Action</th><th>Resource</th><th>Risk</th><th>Why gated</th></tr></thead>
      <tbody>{pending.map(x=><tr key={x.intent_id}><td><b>{x.action_type}</b></td><td>{x.resource}</td><td><Pill tone="amber">{x.risk}</Pill></td><td>{x.reasons.join("; ")}</td></tr>)}</tbody></table></div>
    </Panel>
  </div>;
}

function Departments({data}){
  const mandateByName=Object.fromEntries((data.authority?.mandates||[]).map(x=>[x.department,x]));
  return <div className="page-stack">
    <section className="section-hero"><div>
      <div className="eyebrow"><Building2 size={13}/> Executive departments</div>
      <h1>One organism. Specialized executive organs.</h1>
      <p>Departments are not isolated agents; they share company reality, authority, memory, economics and outcomes.</p>
    </div><Pill tone="violet">{data.departments.length} domains</Pill></section>
    <div className="department-grid">{data.departments.map((dept,i)=>{
      const mandate=mandateByName[dept.name.toLowerCase()];
      const icons=[CircleDollarSign,Target,Activity,Users,Layers3,Workflow,ShieldCheck,Database,Zap,ShieldCheck];
      const Icon=icons[i%icons.length];
      return <div className="department-card" key={dept.name}>
        <div className="dept-top"><span><Icon size={18}/></span><Pill tone={dept.state==="implemented"?"green":"amber"}>{dept.state}</Pill></div>
        <h3>{dept.name}</h3><p>{dept.scope}</p>
        <div className="dept-footer"><span>{dept.source_files} source modules</span>
          <b>{mandate?mandate.remaining+" "+mandate.currency:"shared authority"}</b></div>
      </div>;
    })}</div>
  </div>;
}

function Cognition({data}){
  const learning=data.outcome_learning;
  const correction=data.self_model?.uncertainty?.human_correction_rate;
  return <div className="page-stack">
    <section className="section-hero"><div>
      <div className="eyebrow"><BrainCircuit size={13}/> Executive cognition</div>
      <h1>Forecasts now learn from reality.</h1>
      <p>Independent executive seats produce distributions, not vibes. Later observed outcomes score them and conservatively reweight future pooling.</p>
    </div><Pill tone="violet">{learning.evidence_class}</Pill></section>
    <div className="kpi-grid four">
      <Kpi label="Resolved forecasts" value={num(learning.resolved_forecasts,0)} note="sandbox evidence history" tone="cyan"/>
      <Kpi label="Human correction" value={correction==null?"—":pct(correction)} note="current demo cognition" tone="amber"/>
      <Kpi label="Board roles learned" value={Object.keys(learning.roles||{}).length} note="CRPS role histories" tone="green"/>
      <Kpi label="Models tracked" value={Object.keys(learning.models||{}).length} note="model-version histories" tone="violet"/>
    </div>
    <div className="two-col wide-left">
      <Panel title="Outcome-learned board influence" kicker="CRPS · shrinkage · bounded weights">
        <OutcomeWeightsChart learning={learning}/>
        <p className="panel-foot">Dashed line is neutral weight 1.0. Sparse histories are shrunk toward neutral; influence is bounded to prevent early luck from silencing a seat.</p>
      </Panel>
      <Panel title="Role reliability" kicker="Observed outcome loop">
        <div className="role-list">{Object.entries(learning.roles||{}).map(([role,s])=>
          <div className="role-row" key={role}><div><b>{role.toUpperCase()}</b><span>{s.n} resolved forecasts</span></div>
            <div><b>{num(s.weight,3)}×</b><span>mean rel CRPS {num(s.mean_relative_crps,3)}</span></div></div>)}</div>
      </Panel>
    </div>
    <Panel title="Cognitive constitution" kicker="How ARC thinks">
      <div className="principle-grid">
        <div><BrainCircuit size={18}/><b>Independent first pass</b><span>CEO/CFO/COO/Growth/Risk do not see each other's initial ballot.</span></div>
        <div><GitBranch size={18}/><b>Challenge before consensus</b><span>Dissent survives resolution and remains auditable.</span></div>
        <div><Globe2 size={18}/><b>Simulate material choices</b><span>Digital Twin is a decision instrument, not decorative forecasting.</span></div>
        <div><Target size={18}/><b>Outcome-linked learning</b><span>Observed consequences change future executive weighting.</span></div>
      </div>
    </Panel>
  </div>;
}


function Authority({data}){
  const families=data.autonomy?.families||{};
  return <div className="page-stack">
    <section className="section-hero"><div>
      <div className="eyebrow"><ShieldCheck size={13}/> Authority & autonomy</div>
      <h1>Autonomy is an envelope, not a switch.</h1>
      <p>Customer preset, exclusions, mandates, risk ceilings and emergency controls define what ARC may do.</p>
    </div><Pill tone="cyan">{data.authority?.preset||"—"} preset</Pill></section>
    <div className="two-col">
      <Panel title="Authority mix" kicker="Effective action classes"><AutonomyChart families={families}/></Panel>
      <Panel title="Constitution state" kicker="Owner-controlled boundaries">
        <div className="metric-lines">
          <div><span>Runtime gate</span><b>{data.status?.gate?.mode||"—"}</b></div>
          <div><span>Allowed effects</span><b>{(data.status?.gate?.allowed_effects||[]).join(", ")||"—"}</b></div>
          <div><span>Overrides</span><b>{Object.keys(data.authority?.overrides||{}).length}</b></div>
          <div><span>Mandates</span><b>{(data.authority?.mandates||[]).length}</b></div>
          <div><span>Global pause</span><b>{String(data.status?.gate?.emergency?.global_pause||false)}</b></div>
        </div>
        <div className="callout"><ShieldCheck size={17}/><div><b>Current demo remains sandbox/read-only.</b><span>UI does not expand authority and cannot bypass the Action Firewall.</span></div></div>
      </Panel>
    </div>
    <Panel title="Autonomy matrix" kicker="Normal-language product surface">
      <div className="autonomy-grid">{Object.entries(families).map(([name,value])=>
        <div className="auto-row" key={name}><span>{name.replaceAll("."," · ")}</span>
          <Pill tone={value==="prohibited"?"red":value==="approval_required"?"amber":value==="autonomous_with_notice"?"cyan":"green"}>
            {value.replaceAll("_"," ")}
          </Pill></div>)}</div>
    </Panel>
  </div>;
}

function Integrations({data}){
  return <div className="page-stack">
    <section className="section-hero"><div>
      <div className="eyebrow"><Waypoints size={13}/> Integration fabric</div>
      <h1>Tools are organs. ARC remains the executive system.</h1>
      <p>Integrations should feed the world model, governed action layer, memory and outcome loop — not become a pile of disconnected automations.</p>
    </div><Pill tone="violet">{(data.integrations||[]).length} registered</Pill></section>
    <div className="integration-grid">{(data.integrations||[]).map((item,i)=>
      <div className="integration-card" key={item.provider}>
        <div className="integration-icon">{[Globe2,Database,Target,Activity,Waypoints][i%5]({size:18})}</div>
        <div className="integration-main"><b>{item.name||item.provider}</b><span>{item.provider}</span></div>
        <Pill tone={item.state==="connected"?"green":item.state==="credentials-needed"?"amber":"neutral"}>{item.state}</Pill>
        <p>{item.description||item.detail||"Registered capability"}</p>
      </div>)}</div>
    <Panel title="Integration doctrine" kicker="ADOPT → EXTEND → INVENT">
      <div className="principle-grid">
        <div><Waypoints size={18}/><b>Action connectors</b><span>Write paths remain behind constitution, budgets and firewall.</span></div>
        <div><Database size={18}/><b>Data replication</b><span>Operational history should enter a typed world model with provenance.</span></div>
        <div><Activity size={18}/><b>Observability</b><span>Latency, failures, correction and economic outcome travel with every capability.</span></div>
        <div><Zap size={18}/><b>Capability acquisition</b><span>Free project-local OSS can be benchmarked and adopted without waiting for credentials.</span></div>
      </div>
    </Panel>
  </div>;
}

function Palette({open,setOpen,setSection}){
  if(!open)return null;
  return <div className="cmd-overlay" onMouseDown={()=>setOpen(false)}>
    <div className="cmd-shell" onMouseDown={e=>e.stopPropagation()}>
      <Command label="ARC command palette">
        <div className="cmd-input-row"><Search size={16}/><Command.Input autoFocus placeholder="Navigate the executive organism…"/><kbd>ESC</kbd></div>
        <Command.List><Command.Empty>No command found.</Command.Empty>
          <Command.Group heading="Executive surfaces">{NAV.map(([id,label,Icon])=>
            <Command.Item key={id} value={label} onSelect={()=>{setSection(id);setOpen(false);}}>
              <Icon size={16}/><span>{label}</span><small>Open</small>
            </Command.Item>)}</Command.Group>
        </Command.List>
      </Command>
    </div>
  </div>;
}

async function portalApi(path, options={}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: {"Content-Type":"application/json", ...(options.headers||{})},
    ...options,
  });
  let payload = {};
  try { payload = await response.json(); } catch {}
  if (!response.ok) {
    const error = new Error(payload.error || "request_failed");
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function CompanyRuntimeSwitcher({account,onChanged,compact=false}) {
  const [companies,setCompanies]=useState([]);
  const [busy,setBusy]=useState(false);
  const [creating,setCreating]=useState(false);
  const [name,setName]=useState("");
  const [error,setError]=useState("");

  const refresh=async()=>{
    if(!account?.authenticated)return;
    try{
      const payload=await portalApi("/api/portal/companies");
      setCompanies(payload.companies||[]);
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));}
  };

  useEffect(()=>{refresh();},[account?.user?.org_id]);

  if(!account?.authenticated)return null;
  const active=Number(account.user?.org_id||0);

  const switchCompany=async(orgId)=>{
    orgId=Number(orgId||0);
    if(!orgId||orgId===active)return;
    setBusy(true);setError("");
    try{
      await portalApi("/api/portal/companies/switch",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({csrf:account.csrf,org_id:orgId}),
      });
      await onChanged?.();
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));}
    finally{setBusy(false);}
  };

  const createCompany=async()=>{
    const companyName=name.trim();
    if(companyName.length<2)return;
    setBusy(true);setError("");
    try{
      await portalApi("/api/portal/companies/create",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({csrf:account.csrf,company_name:companyName}),
      });
      setName("");setCreating(false);
      await onChanged?.();
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));}
    finally{setBusy(false);}
  };

  return <div className={cls("company-runtime-switcher",compact&&"compact")}>
    <div className="company-runtime-select">
      <span>active company</span>
      <select disabled={busy} value={active||""} onChange={e=>switchCompany(e.target.value)}>
        {companies.map(company=><option key={company.org_id} value={company.org_id}>
          {company.name} · {company.role}
        </option>)}
      </select>
    </div>
    <button className="company-runtime-new" disabled={busy} onClick={()=>setCreating(v=>!v)}>
      {creating?"Cancel":"+ Company"}
    </button>
    {creating&&<div className="company-runtime-create">
      <input value={name} maxLength={120} autoFocus
        onChange={e=>setName(e.target.value)} placeholder="New company name"
        onKeyDown={e=>{if(e.key==="Enter")createCompany();}}/>
      <button disabled={busy||name.trim().length<2} onClick={createCompany}>
        {busy?"Creating…":"Create & switch"}
      </button>
    </div>}
    {error&&<small className="company-runtime-error">{error}</small>}
  </div>;
}

function AuthModal({mode,setMode,onClose,onAuthenticated,registrationMode="open"}) {
  const [form,setForm]=useState({email:"",password:"",company_name:"",invitation:""});
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const register=mode==="register";

  const submit=async(e)=>{
    e.preventDefault();
    setBusy(true);setError("");
    try{
      const payload=await portalApi(
        register?"/api/portal/register":"/api/portal/login",
        {method:"POST",body:JSON.stringify(form)}
      );
      onAuthenticated(payload);
    }catch(err){
      setError(String(err.message||err));
    }finally{setBusy(false);}
  };

  return <div className="public-modal-backdrop" onMouseDown={onClose}>
    <div className="public-modal" onMouseDown={e=>e.stopPropagation()}>
      <button className="modal-close" onClick={onClose} aria-label="Close"><X size={17}/></button>
      <div className="modal-brand"><div className="brand-mark"><Orbit size={19}/></div><b>ARC</b></div>
      <div className="eyebrow">{register?"Create your company workspace":"Return to ARC"}</div>
      <h2>{register?"Start the executive onboarding":"Sign in to your executive OS"}</h2>
      <p>{register
        ?"Create the owner account first. Billing and authority activation remain separate gates."
        :"Your session opens the company workspace attached to your account."}</p>
      <form onSubmit={submit} className="auth-form">
        {register&&registrationMode==="invite_only"&&<label>
          <span>Private beta invitation code</span>
          <input required maxLength={160} autoComplete="off"
            value={form.invitation} onChange={e=>setForm({...form,invitation:e.target.value})}
            placeholder="One-time access code from Visionare"/>
        </label>}
        {register&&<label><span>Company</span><input required minLength={2} maxLength={120}
          value={form.company_name} onChange={e=>setForm({...form,company_name:e.target.value})}
          placeholder="Northstar Ltd"/></label>}
        <label><span>Email</span><input required type="email" autoComplete="email"
          value={form.email} onChange={e=>setForm({...form,email:e.target.value})}
          placeholder="owner@company.com"/></label>
        <label><span>Password</span><input required type="password" minLength={12} maxLength={128}
          autoComplete={register?"new-password":"current-password"}
          value={form.password} onChange={e=>setForm({...form,password:e.target.value})}
          placeholder="12+ characters"/></label>
        {error&&<div className="form-error">{error.replaceAll("_"," ")}</div>}
        <button className="public-primary full" disabled={busy}>
          {busy?"Working…":register?"Create secure workspace":"Sign in"} <ChevronRight size={15}/>
        </button>
      </form>
      <button className="auth-switch" onClick={()=>{setMode(register?"login":"register");setError("");}}>
        {register?"Already have an account? Sign in":"New to ARC? Create an account"}
      </button>
      <div className="auth-trust"><LockKeyhole size={13}/><span>Passwords are scrypt-hashed. Sessions are server-side and HttpOnly.</span></div>
    </div>
  </div>;
}

function ProductPreview({data}) {
  const pending=data.approvals?.pending?.length||0;
  const dissent=data.dissent?.open_dissent?.length||0;
  const plan=data.plans?.plans?.[0];
  return <div className="product-preview">
    <div className="preview-topbar">
      <div className="preview-brand"><Orbit size={15}/><b>ARC</b><span>{data.company.name}</span></div>
      <div className="preview-live"><StatusDot/><span>executive organism live</span></div>
    </div>
    <div className="preview-body">
      <div className="preview-sidebar">
        {["Executive Deck","Company Organism","Decisions","Departments","Cognition","Authority"].map((x,i)=>
          <span className={i===0?"active":""} key={x}>{x}</span>)}
      </div>
      <div className="preview-main">
        <div className="preview-hero">
          <div><small>CEO briefing</small><b>Your company, continuously understood.</b>
            <span>ARC sees obligations, risks, decisions and authority in one operating model.</span></div>
          <BrainCircuit size={29}/>
        </div>
        <div className="preview-kpis">
          <div><span>Owner gates</span><b>{pending}</b></div>
          <div><span>Open dissent</span><b>{dissent}</b></div>
          <div><span>Autonomy</span><b>{data.authority?.preset||"—"}</b></div>
          <div><span>Mission</span><b>{plan?Math.round(plan.progress*100)+"%":"—"}</b></div>
        </div>
        <div className="preview-grid">
          <div className="preview-card wide"><span>Executive loop</span>
            <div className="mini-loop">{["Observe","Understand","Decide","Simulate","Authorize","Act","Measure","Learn"].map((x,i)=><i key={x}>{i+1}<b>{x}</b></i>)}</div>
          </div>
          <div className="preview-card"><span>Attention</span>
            <b>{pending?"Owner approval required":"No critical owner gate"}</b>
            <small>{dissent} board challenge{dissent===1?"":"s"} retained</small>
          </div>
        </div>
      </div>
    </div>
  </div>;
}

function PublicSite({data,onEnterApp,onOnboarding}) {
  const [authMode,setAuthMode]=useState(null);
  const [account,setAccount]=useState(null);
  const [config,setConfig]=useState({
    billing_available:false,
    plans:{
      growth:{name:"ARC Growth",monthly_gbp:1500,onboarding_gbp:2000,description:"Autonomous executive operating system for a growing company."},
      autopilot:{name:"ARC Autopilot",monthly_gbp:5000,onboarding_gbp:10000,description:"Deep executive autonomy with wider departmental coverage."},
    }
  });
  const [notice,setNotice]=useState("");

  useEffect(()=>{
    portalApi("/api/portal/config").then(setConfig).catch(()=>{});
    portalApi("/api/portal/session").then(payload=>{
      if(payload.authenticated)setAccount(payload);
    }).catch(()=>{});
  },[]);

  const authenticated=(payload)=>{
    setAccount(payload);setAuthMode(null);
    if(payload.subscription?.status==="active"){
      setNotice("Workspace active.");
      onEnterApp();
    }else{
      setNotice("Account ready. Configure company reality, autonomy and integrations before ARC analysis.");
      onOnboarding();
    }
  };

  const checkout=async(planId)=>{
    if(!account){setAuthMode("register");return;}
    if(!config.billing_available){
      setNotice("Registration is available in the configured portal. Paid checkout is disabled until ARC Global passes its own Stripe commissioning.");
      return;
    }
    try{
      const payload=await portalApi("/api/portal/checkout",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({plan_id:planId,csrf:account.csrf}),
      });
      location.href=payload.checkout_url;
    }catch(err){setNotice(String(err.message||err).replaceAll("_"," "));}
  };

  return <div className="public-site">
    <header className="public-nav">
      <a className="public-logo" href="#top"><div className="brand-mark"><Orbit size={20}/></div><div><b>ARC</b><span>Autonomous Executive OS</span></div></a>
      <nav>
        <a href="#how">How it works</a><a href="#system">System</a><a href="#pricing">Pricing</a><a href="#trust">Trust</a>
      </nav>
      <div className="public-nav-actions">
        <button className="public-link" onClick={()=>setAuthMode("login")}><LogIn size={14}/> Sign in</button>
        <button className="public-primary" onClick={()=>account?onOnboarding():setAuthMode("register")}>Start onboarding <ChevronRight size={14}/></button>
      </div>
    </header>

    <main id="top">
      <section className="public-hero">
        <div className="public-hero-copy">
          <div className="public-chip"><Sparkles size={13}/> Global Company OS · controlled customer beta</div>
          <h1>Give your company an executive operating system.</h1>
          <p>
            ARC builds a live model of your company, coordinates executive departments,
            tests material decisions in a Digital Twin, and prepares bounded operations
            through a company-specific authority framework. Live integrations are activated only after verification.
          </p>
          <div className="public-hero-actions">
            <button className="public-primary large" onClick={()=>account?onOnboarding():setAuthMode("register")}>Create your ARC workspace <ChevronRight size={16}/></button>
            <button className="public-secondary large" onClick={()=>location.hash="handover-preview"}>See the delegation experience</button>
          </div>
          <div className="public-proof">
            <span><CheckCircle2 size={14}/> Human-reserved decisions stay human</span>
            <span><CheckCircle2 size={14}/> Every action leaves lineage</span>
            <span><CheckCircle2 size={14}/> Model/provider agnostic</span>
          </div>
        </div>
        <ProductPreview data={data}/>
      </section>

      {notice&&<div className="public-notice"><ShieldCheck size={15}/><span>{notice}</span></div>}

      <section className="public-section" id="how">
        <div className="section-intro">
          <div className="eyebrow">Activation path</div>
          <h2>ARC does not get a master key on day one.</h2>
          <p>Authority expands only after the company model, integrations, constitution and evidence gates are ready.</p>
        </div>
        <div className="activation-grid">
          {[
            [Database,"01","Connect reality","CRM, finance, mail, calendar, support, product and operational systems become one governed company model."],
            [ShieldCheck,"02","Set the constitution","You define reserved decisions, capital limits, risk ceilings, exclusions and approval rules."],
            [Globe2,"03","Build the Digital Twin","ARC models scenarios and consequences before material decisions touch the real company."],
            [BrainCircuit,"04","Activate executive cognition","Independent executive views forecast, challenge and deliberate instead of one model improvising alone."],
            [Zap,"05","Delegate bounded action","ARC executes only inside the authority envelope and preserves receipts, rollback and audit."],
            [Activity,"06","Learn from outcomes","Observed results score forecasts and actions so the operating system adapts rather than repeating mistakes."],
          ].map(([Icon,no,title,copy])=><div className="activation-card" key={title}>
            <div className="activation-icon"><Icon size={20}/><span>{no}</span></div><b>{title}</b><p>{copy}</p>
          </div>)}
        </div>
      </section>

      <section className="public-section ecosystem-section" id="system">
        <div className="section-intro centered">
          <div className="eyebrow">One closed learning loop</div>
          <h2>AVS imagines futures. AVF selects ventures. ARC runs reality.</h2>
          <p>3D belongs inside the decision loop as an explainable spatial view of economic state — not as a decorative city.</p>
        </div>
        <div className="ecosystem-flow">
          <div className="eco-stage"><Globe2 size={23}/><span>AVS</span><b>Artificial economies</b><small>markets, competitors, shocks, causal assumptions</small></div>
          <ChevronRight size={20}/>
          <div className="eco-stage"><Sparkles size={23}/><span>AVF</span><b>Venture factory</b><small>evolve, stress-test, falsify, promote</small></div>
          <ChevronRight size={20}/>
          <div className="eco-stage focus"><Orbit size={23}/><span>ARC</span><b>Real company operator</b><small>departments, decisions, tools, authority, execution</small></div>
          <ChevronRight size={20}/>
          <div className="eco-stage"><Database size={23}/><span>Reality Gap</span><b>Observed outcomes</b><small>recalibrate models with what actually happened</small></div>
        </div>
        <div className="ecosystem-note">
          <div><b>3D Decision World</b><span>Shows flows, bottlenecks, competitive structure and alternative futures with provenance attached.</span></div>
          <div><b>ARC Digital Twin</b><span>Uses the same simulation idea for your actual company before high-impact operating decisions.</span></div>
          <div><b>Handoff contract</b><span>AVF transfers evidence, assumptions, uncertainty and operating requirements — never just a visual object.</span></div>
        </div>
      </section>

      <section className="public-section operator-section">
        <div className="section-intro">
          <div className="eyebrow">Inside the executive OS</div>
          <h2>Not another chatbot. A governed operating layer across the company.</h2>
        </div>
        <div className="operator-grid">
          {[
            [CircleDollarSign,"Finance","cash, budgets, forecasts, obligations, scenario consequences"],
            [Target,"Sales","pipeline, qualification, follow-up, forecasting and bounded negotiation"],
            [Sparkles,"Marketing","research, campaigns, experiments, attribution and competitor response"],
            [Users,"Customer","support, health, escalation and retention feedback"],
            [Layers3,"Product","product signals, roadmap evidence and operating trade-offs"],
            [Building2,"Workforce","capacity, workload, requisitions and organization planning"],
            [Waypoints,"Procurement","supplier decisions, commitments and cost/risk trade-offs"],
            [Workflow,"Technology","SLOs, incidents, change control and engineering operations"],
          ].map(([Icon,title,copy])=><div className="operator-card" key={title}><Icon size={18}/><b>{title}</b><span>{copy}</span></div>)}
        </div>
        <button className="public-secondary system-demo" onClick={onEnterApp}>Open Executive Deck <ChevronRight size={15}/></button>
      </section>

      <section className="public-section pricing-section" id="pricing">
        <div className="section-intro centered">
          <div className="eyebrow">Private beta commercial model</div>
          <h2>Start with bounded executive autonomy. Expand when evidence earns it.</h2>
          <p>Indicative private-beta pricing, subject to final commercial approval. Billing is separate from authority: payment never grants unlimited control over your company.</p>
        </div>
        <div className="pricing-grid">
          {Object.entries(config.plans||{}).map(([id,plan],index)=><div className={cls("price-card",index===1&&"featured")} key={id}>
            {index===1&&<div className="price-ribbon">Deep autonomy</div>}
            <span className="price-name">{plan.name}</span>
            <div className="price"><b>£{num(plan.monthly_gbp,0)}</b><span>/ month</span></div>
            <small>+ £{num(plan.onboarding_gbp,0)} implementation & onboarding</small>
            <p>{plan.description}</p>
            <div className="price-features">
              <span><CheckCircle2 size={13}/> Company ontology + executive deck</span>
              <span><CheckCircle2 size={13}/> Authority constitution + Action Firewall</span>
              <span><CheckCircle2 size={13}/> Departmental executive organs</span>
              <span><CheckCircle2 size={13}/> Decision lineage + outcome learning</span>
              <span><CheckCircle2 size={13}/> Digital Twin decision support</span>
            </div>
            <button className={index===1?"public-primary full":"public-secondary full"} onClick={()=>checkout(id)}>
              {account?"Continue to secure checkout":"Create workspace"} <CreditCard size={14}/>
            </button>
          </div>)}
        </div>
        <div className="billing-state">
          <StatusDot tone={config.billing_available?"green":"amber"}/>
          <span>{config.billing_available
            ?"Secure hosted checkout is connected."
            :"Workspace registration can be tested. Paid subscriptions remain unavailable until ARC Global is commercially commissioned."}</span>
        </div>
      </section>

      <section className="public-section trust-section" id="trust">
        <div className="trust-copy">
          <div className="eyebrow">Trust architecture</div>
          <h2>Control is a product feature, not a disclaimer.</h2>
          <p>ARC is built so the owner can see what it knows, what it believes, what it plans, what it is allowed to do and what actually happened.</p>
        </div>
        <div className="trust-grid">
          <div><ShieldCheck size={20}/><b>Constitutional authority</b><span>reserved decisions, risk ceilings, budgets and exclusions</span></div>
          <div><GitBranch size={20}/><b>Decision lineage</b><span>evidence → forecast → dissent → action → outcome</span></div>
          <div><LockKeyhole size={20}/><b>Tenant isolation</b><span>company state and permissions remain bounded by organization</span></div>
          <div><PlugZap size={20}/><b>Tool governance</b><span>connectors are capabilities; they do not become authority by themselves</span></div>
        </div>
      </section>

      <section className="public-final-cta">
        <div><div className="eyebrow">ARC private beta</div><h2>Build the company model first. Delegate second.</h2>
          <p>Start with onboarding, define the constitution, connect the company, then let ARC earn wider autonomy through evidence.</p></div>
        <button className="public-primary large" onClick={()=>account?onOnboarding():setAuthMode("register")}>Start onboarding <ChevronRight size={16}/></button>
      </section>
    </main>

    <footer className="public-footer">
      <div className="public-logo"><div className="brand-mark"><Orbit size={18}/></div><div><b>ARC</b><span>Autonomous Executive OS</span></div></div>
      <span>Governed autonomy · decision lineage · reality feedback</span>
    </footer>

    {authMode&&<AuthModal mode={authMode} setMode={setAuthMode} onClose={()=>setAuthMode(null)}
      registrationMode={config.registration_mode||"open"} onAuthenticated={authenticated}/>}
  </div>;
}


const ARC_DEPARTMENTS=["finance","sales","marketing","customer","product","workforce","procurement","technology","operations"];

const ONBOARDING_STEPS=[
  ["company","Company",Building2],
  ["objectives","Goals",Target],
  ["finance","Money",CircleDollarSign],
  ["autonomy","Autonomy",BrainCircuit],
  ["integrations","Connections",PlugZap],
  ["constitution","Constitution",ShieldCheck],
  ["briefing","Morning brief",Activity],
  ["review","Activate",Zap],
];

function poundsToPence(value){
  const number=Number(String(value??"").replaceAll(",",""));
  return Number.isFinite(number)?Math.round(number*100):0;
}
function penceToPounds(value){
  if(value==null||value==="")return "";
  return String(Math.round(Number(value)/100));
}
function listFromText(value){
  return String(value||"").split(/\n|,/).map(x=>x.trim()).filter(Boolean);
}
function textFromList(value){return (value||[]).join("\n");}

function IntegrationModal({spec,current,csrf,onClose,onSaved}){
  const methods=spec.methods||[];
  const [method,setMethod]=useState(current?.method||spec.preferred||methods[0]||"");
  const [metadata,setMetadata]=useState(current?.metadata||{});
  const [secrets,setSecrets]=useState({});
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const requiresSecret=(spec.secret_fields||[]).length>0&&method!=="oauth"&&method!=="open_banking"&&method!=="github_app"&&method!=="upload";

  const save=async()=>{
    setBusy(true);setError("");
    const status=(["oauth","open_banking","github_app"].includes(method))
      ?"oauth_pending"
      :"configured";
    try{
      const payload=await portalApi("/api/portal/integrations/configure",{
        method:"POST",
        headers:{"X-CSRF-Token":csrf},
        body:JSON.stringify({
          integration_id:spec.id,
          method,
          metadata,
          secrets,
          status,
          csrf,
        }),
      });
      onSaved(payload.onboarding);
      onClose();
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));}
    finally{setBusy(false);}
  };

  return <div className="public-modal-backdrop" onMouseDown={onClose}>
    <div className="integration-modal" onMouseDown={e=>e.stopPropagation()}>
      <button className="modal-close" onClick={onClose} aria-label="Close"><X size={17}/></button>
      <div className="kicker">{spec.group} integration</div>
      <h2>{spec.name}</h2>
      <p>{spec.description}</p>

      <div className="method-tabs">
        {methods.map(item=><button key={item} className={cls(method===item&&"active")} onClick={()=>setMethod(item)}>
          {item.replaceAll("_"," ")}
        </button>)}
      </div>

      {["oauth","open_banking","github_app"].includes(method)&&
        <div className="oauth-notice"><KeyRound size={16}/><div>
          <b>Delegated connection preferred</b>
          <span>The provider authorization flow is kept separate from ARC credentials. This build records the requested connection but does not pretend OAuth completed until the provider callback exists.</span>
        </div></div>}

      <div className="integration-form">
        {(spec.nonsecret_fields||[]).map(field=><label key={field}>
          <span>{field.replaceAll("_"," ")}</span>
          <input value={metadata[field]||""} onChange={e=>setMetadata({...metadata,[field]:e.target.value})}
            placeholder={field==="url"?"https://…":field}/>
        </label>)}
        {requiresSecret&&(spec.secret_fields||[]).map(field=><label key={field}>
          <span>{field.replaceAll("_"," ")} · encrypted vault</span>
          <input type="password" autoComplete="off" value={secrets[field]||""}
            onChange={e=>setSecrets({...secrets,[field]:e.target.value})}
            placeholder={current?.has_secret?"stored — leave blank to keep":"paste scoped credential"}/>
        </label>)}
      </div>

      <div className="integration-scope">
        <b>ARC capability request</b>
        <div>{(spec.capabilities||[]).map(cap=><span key={cap}>{cap}</span>)}</div>
      </div>
      {error&&<div className="form-error">{error}</div>}
      <div className="modal-actions">
        <button className="public-secondary" onClick={onClose}>Cancel</button>
        <button className="public-primary" disabled={busy} onClick={save}>
          {busy?"Saving…":requiresSecret?"Encrypt & save":"Continue connection"} <ChevronRight size={14}/>
        </button>
      </div>
    </div>
  </div>;
}


function formatStrategyMetric(metric,value){
  if(value==null||Number.isNaN(Number(value)))return "—";
  const n=Number(value);
  if(metric.includes("pence"))return (n<0?"−":"")+"£"+Math.abs(n/100).toLocaleString(undefined,{maximumFractionDigits:0});
  if(metric.includes("rate")||metric==="survival"||metric.includes("margin")||metric==="ending_reputation")return (n*100).toFixed(1)+"%";
  return n.toLocaleString(undefined,{maximumFractionDigits:2});
}

function StrategyLab({account}){
  const [lab,setLab]=useState(null);
  const [family,setFamily]=useState("");
  const [draft,setDraft]=useState({});
  const [experiment,setExperiment]=useState({intervention_id:"",factor:"",months:18,start_month:0,replicates:24});
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");

  const hydrateLab=(payload,forcedFamily)=>{
    setLab(payload);
    const selected=forcedFamily||payload.profile?.model_family||payload.readiness?.model_family||payload.routing?.selected_model_family||"";
    setFamily(selected);
    const contract=payload.models?.[selected];
    const stored=payload.profile?.model_family===selected?(payload.profile?.profile||{}):{};
    const next={};
    Object.keys(contract?.fields||{}).forEach(key=>{
      if(stored[key]!=null)next[key]=String(stored[key]);
      else if(payload.exact_prefill?.[key]?.value!=null)next[key]=String(payload.exact_prefill[key].value);
      else next[key]="";
    });
    setDraft(next);
    const interventions=contract?.interventions||{};
    const current=experiment.intervention_id;
    const first=Object.keys(interventions)[0]||"";
    const chosen=interventions[current]?current:first;
    const spec=interventions[chosen];
    setExperiment(prev=>({
      ...prev,
      intervention_id:chosen,
      factor:spec?.default_factor!=null?String(spec.default_factor):"",
      start_month:spec?.kind==="profile_multiplier"?0:prev.start_month,
    }));
  };

  const load=async()=>{
    setError("");
    try{
      const payload=await portalApi("/api/portal/strategy-lab");
      hydrateLab(payload);
      return payload;
    }catch(err){
      setError(String(err.message||err).replaceAll("_"," "));
      throw err;
    }
  };

  useEffect(()=>{if(account?.authenticated)load().catch(()=>{});},[account?.user?.org_id]);

  const chooseFamily=id=>{
    const contract=lab?.models?.[id];
    if(!contract)return;
    setFamily(id);
    const stored=lab?.profile?.model_family===id?(lab.profile.profile||{}):{};
    const next={};
    Object.keys(contract.fields||{}).forEach(key=>{
      if(stored[key]!=null)next[key]=String(stored[key]);
      else if(lab?.exact_prefill?.[key]?.value!=null)next[key]=String(lab.exact_prefill[key].value);
      else next[key]="";
    });
    setDraft(next);
    const first=Object.keys(contract.interventions||{})[0]||"";
    const spec=contract.interventions?.[first];
    setExperiment(prev=>({...prev,intervention_id:first,factor:spec?.default_factor!=null?String(spec.default_factor):"",start_month:spec?.kind==="profile_multiplier"?0:prev.start_month}));
  };

  const saveProfile=async()=>{
    const contract=lab?.models?.[family];
    if(!contract||!account?.csrf)return;
    const profile={};
    for(const [key,spec] of Object.entries(contract.fields||{})){
      const raw=draft[key];
      if(raw==null||String(raw).trim()==="")continue;
      const value=Number(raw);
      if(!Number.isFinite(value)){setError(spec.label+" must be numeric.");return;}
      profile[key]=spec.type==="integer"?Math.trunc(value):value;
    }
    setBusy(true);setError("");
    try{
      const payload=await portalApi("/api/portal/strategy-lab/profile",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({csrf:account.csrf,model_family:family,profile}),
      });
      hydrateLab(payload,family);
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));}
    finally{setBusy(false);}
  };

  const updateLocalJob=job=>setLab(prev=>{
    if(!prev)return prev;
    return {...prev,jobs:[job,...(prev.jobs||[]).filter(item=>item.job_id!==job.job_id)]};
  });

  const pollJob=async jobId=>{
    for(let i=0;i<80;i++){
      const payload=await portalApi("/api/portal/strategy-lab/jobs/"+jobId);
      updateLocalJob(payload.job);
      if(["ready","failed"].includes(payload.job?.status))return payload.job;
      await new Promise(resolve=>setTimeout(resolve,500));
    }
    throw new Error("strategy experiment is still running; it remains safely persisted");
  };

  const runExperiment=async()=>{
    const contract=lab?.models?.[family];
    const spec=contract?.interventions?.[experiment.intervention_id];
    if(!spec||!account?.csrf)return;
    setBusy(true);setError("");
    try{
      const body={
        csrf:account.csrf,
        intervention_id:experiment.intervention_id,
        months:Number(experiment.months),
        start_month:spec.kind==="profile_multiplier"?0:Number(experiment.start_month),
        replicates:Number(experiment.replicates),
      };
      if(String(experiment.factor).trim()!=="")body.factor=Number(experiment.factor);
      const submitted=await portalApi("/api/portal/strategy-lab/experiments",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify(body),
      });
      updateLocalJob(submitted.job);
      await pollJob(submitted.job.job_id);
      await load();
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));}
    finally{setBusy(false);}
  };

  if(!account?.authenticated)return <div className="page-stack strategy-lab">
    <section className="strategy-hero">
      <div><div className="eyebrow"><Layers3 size={13}/> ARC Strategy Lab</div><h1>Private strategy simulation is bound to a real company runtime.</h1>
      <p>The public system demo does not run customer simulations. Create or sign into a company workspace to build a model contract, run paired future worlds and retain evidence under that tenant only.</p></div>
      <div className="strategy-runtime-seal"><span>public preview</span><b>NO TENANT CONTEXT</b><small>no company data sent to simulation</small></div>
    </section>
    <Panel title="Strategy Lab stays behind the company boundary" kicker="privacy + authority by construction">
      <div className="strategy-experiment-doctrine">
        <div><ShieldCheck size={13}/><b>company runtime required</b></div>
        <div><CheckCircle2 size={13}/><b>model inputs are explicit, never guessed</b></div>
        <div><CheckCircle2 size={13}/><b>simulation remains model evidence, not authority</b></div>
        <button className="primary" onClick={()=>{location.hash="onboarding";}}>Open a company workspace</button>
      </div>
    </Panel>
  </div>;

  if(!lab)return <div className="strategy-loading"><Activity size={16}/><span>Loading the active company model…</span>{error&&<small>{error}</small>}</div>;

  const contract=lab.models?.[family]||lab.selected_contract;
  const readiness=lab.readiness||{};
  const displayedMissing=family===readiness.model_family
    ?(readiness.missing_fields||[])
    :Object.keys(contract?.fields||{}).filter(key=>String(draft[key]??"").trim()==="");
  const displayedReady=family===readiness.model_family&&Boolean(readiness.simulation_allowed);
  const candidates=lab.routing?.candidates||[];
  const intervention=contract?.interventions?.[experiment.intervention_id];
  const jobs=lab.jobs||[];

  return <div className="page-stack strategy-lab">
    <section className="strategy-hero">
      <div>
        <div className="eyebrow"><Layers3 size={13}/> ARC Strategy Lab · counterfactual decision science</div>
        <h1>Test consequential decisions before the company pays for them.</h1>
        <p>ARC binds every experiment to <b>{lab.runtime?.active_company_name}</b>, preserves the current company-model digest and keeps simulation evidence separate from real execution authority.</p>
      </div>
      <div className="strategy-runtime-seal">
        <span>company runtime</span>
        <b className="mono">{lab.runtime?.runtime_ref}</b>
        <small className="mono">{lab.runtime?.context_digest?.slice(0,18)}… context</small>
      </div>
    </section>

    <div className="strategy-kpis">
      <Kpi label="Model routing" value={(lab.routing?.routing_state||"UNKNOWN").replaceAll("_"," ")} note={lab.routing?.selection_source?.replaceAll("_"," ")||"model-family suggestion"} tone="cyan"/>
      <Kpi label="Model readiness" value={(family===readiness.model_family?(readiness.readiness_state||"NOT CONFIGURED"):"UNSAVED MODEL SELECTION").replaceAll("_"," ")} note={(contract?Object.keys(contract.fields||{}).length-displayedMissing.length:0)+"/"+(contract?Object.keys(contract.fields||{}).length:0)+" required inputs present"} tone={displayedReady?"green":"amber"}/>
      <Kpi label="Evidence class" value="MODEL ONLY" note="never empirical by simulation alone" tone="violet"/>
      <Kpi label="Real authority" value="0" note="Strategy Lab cannot execute company actions" tone="green"/>
    </div>

    {error&&<div className="strategy-error">{error}</div>}

    <Panel title="Model Fit" kicker="domain routing · explicit selection remains reversible">
      <div className="strategy-model-grid">
        {Object.entries(lab.models||{}).map(([id,model])=>{
          const candidate=candidates.find(item=>item.model_family===id);
          return <button key={id} className={cls("strategy-model-card",family===id&&"selected")} onClick={()=>chooseFamily(id)}>
            <div><span>{id}</span><Pill tone={candidate?.score>0?"cyan":"neutral"}>{candidate?.score||0} fit signals</Pill></div>
            <b>{model.label}</b>
            <p>{model.description}</p>
            <small>{candidate?.matched_keywords?.length?"matched: "+candidate.matched_keywords.join(", "):"no declared-domain match; owner may still select explicitly"}</small>
          </button>;
        })}
      </div>
      <div className="strategy-truth"><ShieldCheck size={14}/><span>{lab.routing?.truth_note||lab.truth_note}</span></div>
    </Panel>

    {contract&&<Panel title="Company Model Contract" kicker={contract.label+" · "+contract.model_version}>
      <div className="strategy-contract-head">
        <div><b>{displayedMissing.length} missing inputs</b><span>Only explicit company parameters enter the simulator. ARC does not synthesize missing economics.</span></div>
        <button className="primary" disabled={busy} onClick={saveProfile}>{busy?"Working…":"Save model profile"}</button>
      </div>
      <div className="strategy-field-grid">
        {Object.entries(contract.fields||{}).map(([key,spec])=><label key={key} className={cls("strategy-field",displayedMissing.includes(key)&&"missing")}>
          <span>{spec.label}</span>
          <input type="number" step={spec.type==="integer"?"1":"any"} min={spec.minimum??undefined} max={spec.maximum??undefined}
            value={draft[key]??""} onChange={e=>setDraft(prev=>({...prev,[key]:e.target.value}))}/>
          <small>{spec.unit||spec.description||key}</small>
          {lab.exact_prefill?.[key]&&<i>exact prefill · {lab.exact_prefill[key].source}</i>}
        </label>)}
      </div>
      <div className="strategy-readiness-line">
        <Pill tone={displayedReady?"green":"amber"}>{(family===readiness.model_family?(readiness.readiness_state||"NOT CONFIGURED"):"UNSAVED MODEL SELECTION").replaceAll("_"," ")}</Pill>
        <span>{displayedReady?"Profile schema is complete. Simulation is permitted, not real execution.":displayedMissing.length?"Complete: "+displayedMissing.join(", "):"Save this model profile to run its readiness check."}</span>
      </div>
    </Panel>}

    {contract&&<Panel title="Counterfactual Ensemble" kicker="paired multi-seed worlds · uncertainty first">
      <div className="strategy-experiment-layout">
        <div className="strategy-experiment-form">
          <label><span>Intervention</span><select value={experiment.intervention_id} onChange={e=>{
            const id=e.target.value;const spec=contract.interventions?.[id];
            setExperiment(prev=>({...prev,intervention_id:id,factor:spec?.default_factor!=null?String(spec.default_factor):"",start_month:spec?.kind==="profile_multiplier"?0:prev.start_month}));
          }}>{Object.entries(contract.interventions||{}).map(([id,spec])=><option key={id} value={id}>{spec.label}</option>)}</select></label>
          <label><span>Factor</span><input type="number" step="0.01" value={experiment.factor} onChange={e=>setExperiment({...experiment,factor:e.target.value})}/><small>{intervention?String(intervention.minimum)+"–"+String(intervention.maximum):"bounded by model contract"}</small></label>
          <label><span>Horizon</span><input type="number" min="1" max="36" value={experiment.months} onChange={e=>setExperiment({...experiment,months:e.target.value})}/><small>months · max 36</small></label>
          <label><span>Intervention month</span><input type="number" min="0" max={Math.max(0,Number(experiment.months)-1)} disabled={intervention?.kind==="profile_multiplier"} value={intervention?.kind==="profile_multiplier"?0:experiment.start_month} onChange={e=>setExperiment({...experiment,start_month:e.target.value})}/><small>{intervention?.kind==="profile_multiplier"?"policy comparison begins at origin":"baseline and child share pre-intervention world"}</small></label>
          <label><span>Paired worlds</span><input type="number" min="4" max="64" value={experiment.replicates} onChange={e=>setExperiment({...experiment,replicates:e.target.value})}/><small>4–64 deterministic seed pairs</small></label>
          <button className="primary strategy-run" disabled={busy||!readiness.simulation_allowed||!experiment.intervention_id} onClick={runExperiment}>
            <GitBranch size={14}/>{busy?"Running bounded ensemble…":"Run strategy experiment"}
          </button>
        </div>
        <div className="strategy-experiment-doctrine">
          <span>What ARC will preserve</span>
          <div><CheckCircle2 size={13}/><b>same seed inside each baseline/child pair</b></div>
          <div><CheckCircle2 size={13}/><b>model + world + context version provenance</b></div>
          <div><CheckCircle2 size={13}/><b>distributional deltas, not one lucky run</b></div>
          <div><ShieldCheck size={13}/><b>no authority or connector execution</b></div>
          <small>{lab.truth_note}</small>
        </div>
      </div>
    </Panel>}

    <Panel title="Evidence Ledger" kicker={String(jobs.length)+" tenant-bound strategy "+(jobs.length===1?"experiment":"experiments")}>
      {jobs.length===0?<div className="strategy-empty">No Strategy Lab evidence has been generated for this company runtime yet.</div>:
      <div className="strategy-job-list">{jobs.map(job=>{
        const ev=job.evidence;
        const distributions=ev?.distributions||{};
        return <article className="strategy-job" key={job.job_id}>
          <div className="strategy-job-head">
            <div><span>{job.request?.model_family||ev?.model_family||"model"} · {job.request?.intervention_id||ev?.intervention?.id}</span><b>{job.status?.toUpperCase()}</b></div>
            <div><code>{job.job_id}</code><small>{ev?String(ev.replicates)+" paired worlds · "+ev.empirical_status:"evidence pending"}</small></div>
          </div>
          {ev&&<div className="strategy-distribution-grid">
            {Object.entries(distributions).slice(0,8).map(([metric,dist])=><div className="strategy-distribution" key={metric}>
              <span>{metric.replaceAll("_"," ")}</span>
              <b>{formatStrategyMetric(metric,dist.median)}</b>
              <small>P10 {formatStrategyMetric(metric,dist.p10)} · P90 {formatStrategyMetric(metric,dist.p90)}</small>
              <i>{pct(dist.positive_rate||0,0)} positive · {pct(dist.sign_consistency||0,0)} sign consistency</i>
            </div>)}
          </div>}
          {ev?.sign_conflict_metrics?.length>0&&<div className="strategy-conflicts"><BrainCircuit size={13}/><span>Effect changes sign across plausible worlds: {ev.sign_conflict_metrics.join(", ")}</span></div>}
          {ev&&<div className="strategy-evidence-foot"><span>MODEL EVIDENCE · NOT EMPIRICAL</span><code>{ev.sha256?.slice(0,18)}…</code><b>authority=false</b></div>}
        </article>;
      })}</div>}
    </Panel>
  </div>;
}


function OnboardingExperience({data,onEnterApp,onExit}){
  const [account,setAccount]=useState(null);
  const [state,setState]=useState(null);
  const [catalog,setCatalog]=useState([]);
  const [step,setStep]=useState("company");
  const [showHandoverGuide,setShowHandoverGuide]=useState(true);
  const [handover,setHandover]=useState(null);
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [integration,setIntegration]=useState(null);
  const [activationAnalysis,setActivationAnalysis]=useState(null);
  const [activationHash,setActivationHash]=useState("");
  const [ventureHandoff,setVentureHandoff]=useState(null);
  const [ventureEvidence,setVentureEvidence]=useState([]);
  const [ventureEvidencePortfolio,setVentureEvidencePortfolio]=useState(null);
  const [evidenceJobId,setEvidenceJobId]=useState("");
  const [evidenceBusy,setEvidenceBusy]=useState(false);
  const [forms,setForms]=useState({});

  const hydrate=(snapshot)=>{
    setState(snapshot);
    setForms({
      company:{
        name:snapshot.company?.name||"",
        website:snapshot.company?.website||"",
        industry:snapshot.company?.industry||"",
        markets:textFromList(snapshot.company?.markets),
        products:textFromList(snapshot.company?.products),
        team_size:snapshot.company?.team_size??"",
        currency:snapshot.company?.currency||"GBP",
        timezone:snapshot.company?.timezone||"Europe/London",
        business_hours:snapshot.company?.business_hours||"",
        price_list:snapshot.company?.price_list||"",
        customer_faq:snapshot.company?.customer_faq||"",
        booking_policy:snapshot.company?.booking_policy||"",
        staff_roles:snapshot.company?.staff_roles||"",
        fulfilment_sop:snapshot.company?.fulfilment_sop||"",
        brand_voice:snapshot.company?.brand_voice||"",
        emergency_rules:snapshot.company?.emergency_rules||"",
      },
      objectives:{
        goals:textFromList(snapshot.objectives?.goals),
        owner_priorities:textFromList(snapshot.objectives?.owner_priorities),
        constraints:textFromList(snapshot.objectives?.constraints),
      },
      finance:{
        monthly_revenue_gbp:penceToPounds(snapshot.finance?.monthly_revenue_pence),
        cash_gbp:penceToPounds(snapshot.finance?.cash_pence),
        monthly_operating_budget_gbp:penceToPounds(snapshot.finance?.monthly_operating_budget_pence),
        minimum_cash_buffer_gbp:penceToPounds(snapshot.finance?.minimum_cash_buffer_pence),
        arc_discretionary_budget_gbp:penceToPounds(snapshot.finance?.arc_discretionary_budget_pence),
        single_action_limit_gbp:penceToPounds(snapshot.finance?.single_action_limit_pence),
        department_budgets_gbp:Object.fromEntries(
          ARC_DEPARTMENTS.map(name=>[name,penceToPounds(snapshot.finance?.department_budgets_pence?.[name])])
        ),
      },
      autonomy:{
        preset:snapshot.autonomy?.preset||"LIGHT",
        department_overrides:snapshot.autonomy?.department_overrides||{},
        action_overrides:snapshot.autonomy?.action_overrides||{},
      },
      constitution:{
        reserved_decisions:snapshot.constitution?.reserved_decisions||[
          "change_company_ownership","borrow_or_issue_equity","sign_material_legal_commitment",
          "hire_or_terminate_executive","move_money_above_owner_limit","change_constitution",
          "grant_new_external_authority",
        ],
        prohibited_actions:textFromList(snapshot.constitution?.prohibited_actions),
        risk_tolerance:snapshot.constitution?.risk_tolerance||"balanced",
        legal_notes:snapshot.constitution?.legal_notes||"",
        brand_rules:snapshot.constitution?.brand_rules||"",
        approval_contacts:snapshot.constitution?.approval_contacts||[],
      },
      briefing:{
        hour:snapshot.briefing?.hour??7,
        minute:snapshot.briefing?.minute??30,
        channel:snapshot.briefing?.channel||"portal",
        include_weekends:snapshot.briefing?.include_weekends??true,
      },
      review:{
        confirmed:snapshot.review?.confirmed||false,
        owner_acknowledged_authority:snapshot.review?.owner_acknowledged_authority||false,
        owner_acknowledged_secrets:snapshot.review?.owner_acknowledged_secrets||false,
      },
    });
  };

  const applyHandoverTemplate=(key)=>{
    const example=key==="siamluxe"?{
      company:{name:"Siam Luxe",website:"",industry:"Premium tourism and romantic experiences",
        markets:"Phuket, Thailand",products:"Private sunset cruise\nRomantic proposal package\nPrivate dinner and photography",
        currency:"THB",timezone:"Asia/Bangkok"},
      objectives:{goals:"Grow profitable confirmed bookings\nMaintain high guest satisfaction\nReduce manual coordination workload",
        owner_priorities:"Safety of guests and crew\nReliable fulfilment",
        constraints:"Captain has final authority over marine safety\nNever promise an unavailable slot\nNo unapproved financial commitments"},
    }:{
      company:{name:"CamdenSiam",website:"",industry:"Photography studio and tourism experiences",
        markets:"Patong, Phuket, Thailand",products:"Vintage gangster photoshoots\nThai vintage photoshoots\nCouples photo sessions",
        currency:"THB",timezone:"Asia/Bangkok"},
      objectives:{goals:"Convert walk-in visitors into paid photo sessions\nDeliver photos on time with high customer satisfaction\nIncrease repeat bookings and gross margin",
        owner_priorities:"Guest consent and privacy\nQuality and transparent prices",
        constraints:"Never publish client photographs without permission\nManager must review exceptional refunds\nNever exceed approved marketing budgets"},
    };
    if((state?.objectives?.goals||[]).length && !window.confirm(
      "This example changes the company and goals in the unsaved form. Your existing server data stays unchanged until you click Save. Continue?"
    ))return;
    setForms(previous=>({...previous,
      company:{...previous.company,...example.company},
      objectives:{...previous.objectives,...example.objectives},
    }));
    setError("");
    setStep("company");
    setShowHandoverGuide(false);
  };

  const load=async()=>{
    const session=await portalApi("/api/portal/session");
    if(!session.authenticated){location.hash="";return;}
    setAccount(session);
    const [onboarding,integrations,activation,venture,evidence,handoverResponse]=await Promise.all([
      portalApi("/api/portal/onboarding"),
      portalApi("/api/portal/integrations"),
      portalApi("/api/portal/activation"),
      portalApi("/api/portal/venture-handoff"),
      portalApi("/api/portal/venture-evidence"),
      portalApi("/api/portal/handover-readiness"),
    ]);
    hydrate(onboarding.onboarding);
    setCatalog(integrations.catalog||[]);
    setActivationAnalysis(activation.analysis?.analysis||null);
    setActivationHash(activation.manifest?.manifest_hash||"");
    setVentureHandoff(venture.handoff||null);
    setVentureEvidence(evidence.evidence||[]);
    setVentureEvidencePortfolio(evidence.portfolio||null);
    setHandover(handoverResponse.handover||null);
    const current=onboarding.onboarding.stage;
    if(ONBOARDING_STEPS.some(([id])=>id===current))setStep(current);
  };

  useEffect(()=>{load().catch(err=>setError(String(err.message||err)));},[]);

  const reloadActiveCompany=async()=>{
    setError("");
    setState(null);
    setActivationAnalysis(null);
    setActivationHash("");
    setVentureHandoff(null);
    setVentureEvidence([]);
    setVentureEvidencePortfolio(null);
    setShowHandoverGuide(true);
    await load();
  };

  if(!account||!state||!forms.company)return <div className="boot"><div className="boot-orb"><Orbit size={28}/></div><span>Building your company control plane…</span></div>;

  const stepIndex=ONBOARDING_STEPS.findIndex(([id])=>id===step);
  const form=forms[step]||{};
  const setForm=(patch)=>setForms({...forms,[step]:{...form,...patch}});
  const next=()=>setStep(ONBOARDING_STEPS[Math.min(stepIndex+1,ONBOARDING_STEPS.length-1)][0]);
  const prev=()=>setStep(ONBOARDING_STEPS[Math.max(stepIndex-1,0)][0]);

  const serialize=()=>{
    if(step==="company")return {...form,
      markets:listFromText(form.markets),products:listFromText(form.products),
      team_size:Number(form.team_size||0)};
    if(step==="objectives")return {
      goals:listFromText(form.goals),
      owner_priorities:listFromText(form.owner_priorities),
      constraints:listFromText(form.constraints),
    };
    if(step==="finance")return {
      monthly_revenue_pence:poundsToPence(form.monthly_revenue_gbp),
      cash_pence:poundsToPence(form.cash_gbp),
      monthly_operating_budget_pence:poundsToPence(form.monthly_operating_budget_gbp),
      minimum_cash_buffer_pence:poundsToPence(form.minimum_cash_buffer_gbp),
      arc_discretionary_budget_pence:poundsToPence(form.arc_discretionary_budget_gbp),
      single_action_limit_pence:poundsToPence(form.single_action_limit_gbp),
      department_budgets_pence:Object.fromEntries(
        ARC_DEPARTMENTS.map(name=>[name,poundsToPence(form.department_budgets_gbp?.[name]||0)])
      ),
    };
    if(step==="constitution")return {...form,
      prohibited_actions:listFromText(form.prohibited_actions)};
    return form;
  };

  const save=async({advance=true}={})=>{
    if(step==="integrations"){if(advance)next();return true;}
    setBusy(true);setError("");
    try{
      const payload=await portalApi("/api/portal/onboarding/stage",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({stage:step,data:serialize(),csrf:account.csrf}),
      });
      setState(payload.onboarding);
      portalApi("/api/portal/handover-readiness").then(x=>setHandover(x.handover)).catch(()=>{});
      if(advance)next();
      return true;
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));return false;}
    finally{setBusy(false);}
  };

  const activate=async()=>{
    setBusy(true);setError("");
    try{
      const saved=await save({advance:false});
      if(!saved)return;
      const payload=await portalApi("/api/portal/activate",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({csrf:account.csrf}),
      });
      setState(payload.onboarding);
      setActivationAnalysis(payload.preingestion_analysis||null);
      setActivationHash(payload.activation_manifest_hash||"");
      portalApi("/api/portal/handover-readiness").then(x=>setHandover(x.handover)).catch(()=>{});
    }catch(err){setError(String(err.message||err).replaceAll("_"," "));}
    finally{setBusy(false);}
  };

  const pullVentureEvidence=async()=>{
    const jobId=evidenceJobId.trim();
    if(!/^fork_[a-f0-9]{24}$/.test(jobId)){
      setError("Enter a valid AVF fork job ID.");
      return;
    }
    setEvidenceBusy(true);setError("");
    try{
      const payload=await portalApi("/api/portal/venture-evidence/pull",{
        method:"POST",
        headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({csrf:account.csrf,job_id:jobId}),
      });
      const summary=payload.evidence;
      setVentureEvidence(prev=>[
        summary,
        ...prev.filter(item=>item.sha256!==summary.sha256),
      ]);
      setVentureEvidencePortfolio(payload.portfolio||null);
      setEvidenceJobId("");
    }catch(err){
      setError(String(err.message||err).replaceAll("_"," "));
    }finally{
      setEvidenceBusy(false);
    }
  };

  const modeDetails={
    LIGHT:{
      label:"LIGHT CEO",tag:"Advise & prepare",
      copy:"ARC reads company reality, analyses, forecasts and prepares work. Material actions remain owner-gated.",
      bullets:["Autonomous analysis & briefs","Autonomous drafts","Owner approval for external effects"],
    },
    MEDIUM:{
      label:"MEDIUM CEO",tag:"Operate routine work",
      copy:"ARC runs routine operational preparation and selected low-risk updates while external effects stay deliberately gated.",
      bullets:["Routine CRM & preparation","Continuous executive monitoring","Approval for sends, posts and material spend"],
    },
    FULL:{
      label:"FULL CEO",tag:"Bounded autonomous operator",
      copy:"ARC runs eligible day-to-day operations autonomously-with-notice inside budgets, exclusions and the owner Constitution.",
      bullets:["Routine external execution","Cross-department delegation","Owner retains reserved legal/capital decisions"],
    },
  };

  const renderStep=()=>{
    if(step==="company")return <div className="setup-pane">
      <div className="setup-heading"><Building2 size={21}/><div><span>01 · Company reality</span><h2>Teach ARC what business it is operating.</h2><p>Start with stable facts. ARC will later enrich this from connected systems instead of repeatedly asking you.</p></div></div>
      <div className="form-grid two">
        <label><span>Company name</span><input value={form.name} onChange={e=>setForm({name:e.target.value})}/></label>
        <label><span>Website</span><input value={form.website} onChange={e=>setForm({website:e.target.value})} placeholder="https://company.com"/></label>
        <label><span>Industry</span><input value={form.industry} onChange={e=>setForm({industry:e.target.value})} placeholder="B2B SaaS, retail, services…"/></label>
        <label><span>Team size</span><input type="number" min="0" value={form.team_size} onChange={e=>setForm({team_size:e.target.value})}/></label>
        <label><span>Markets / regions</span><textarea value={form.markets} onChange={e=>setForm({markets:e.target.value})} placeholder={"United Kingdom\nEuropean Union"}/></label>
        <label><span>Products / services</span><textarea value={form.products} onChange={e=>setForm({products:e.target.value})} placeholder={"Core platform\nEnterprise implementation"}/></label>
        <label><span>Reporting currency</span><input value={form.currency} maxLength={3} onChange={e=>setForm({currency:e.target.value.toUpperCase()})}/></label>
        <label><span>Company timezone</span><input value={form.timezone} onChange={e=>setForm({timezone:e.target.value})}/></label>
      </div>
      <details className="operating-manual"><summary><BookOpen size={17}/> CEO Operations Playbook <span>Optional now · essential before real customer autonomy</span></summary>
        <p>Write how your business actually works. ARC uses this as declared operating knowledge—not verified truth. Never include personal passwords.</p>
        <div className="form-grid two">{[
          ["business_hours","Opening hours and timezone","Example: Daily 10:00–20:00 Asia/Bangkok, exceptions listed separately"],
          ["price_list","Confirmed services and price list","List actual prices, inclusions, currency, extras and taxes. Never guess prices."],
          ["customer_faq","Customer questions and approved answers","FAQ about location, packages, age restrictions, delivery, reservations and support."],
          ["booking_policy","Reservation, deposit, cancellation and refund rules","Explain deposits, cut-off times, availability source, no-show and rescheduling rules."],
          ["staff_roles","Team roles and responsibility map","Manager, front desk, photographers, agents and escalation owners. Roles first, private data only if required."],
          ["fulfilment_sop","Step-by-step service delivery","From enquiry and quotation through payment, booking, fulfilment, feedback and reconciliation."],
          ["brand_voice","Brand personality and communication rules","Languages, tone, response style, promises and forbidden claims."],
          ["emergency_rules","Safety, legal and emergency escalations","When ARC must stop, who reviews complaints, medical safety, contracts and incident response."],
        ].map(([field,label,placeholder])=><label key={field}><span>{label}</span><textarea maxLength={4000} placeholder={placeholder} value={form[field]||""} onChange={e=>setForm({[field]:e.target.value})}/></label>)}</div>
      </details>
    </div>;

    if(step==="objectives")return <div className="setup-pane">
      <div className="setup-heading"><Target size={21}/><div><span>02 · Objectives</span><h2>Define what winning means.</h2><p>ARC optimizes a portfolio of objectives, not one vanity metric. Constraints remain explicit.</p></div></div>
      <div className="form-grid">
        <label><span>Business goals · one per line</span><textarea className="large-textarea" value={form.goals} onChange={e=>setForm({goals:e.target.value})} placeholder={"Grow recurring revenue 30%\nProtect 6 months cash runway\nReduce founder operational load"}/></label>
        <label><span>Owner priorities</span><textarea value={form.owner_priorities} onChange={e=>setForm({owner_priorities:e.target.value})} placeholder={"Customer trust\nMargin before growth"}/></label>
        <label><span>Hard constraints</span><textarea value={form.constraints} onChange={e=>setForm({constraints:e.target.value})} placeholder={"Do not enter regulated markets\nNo layoffs without owner decision"}/></label>
      </div>
    </div>;

    if(step==="finance"){
      const currency=(forms.company?.currency||"GBP").toUpperCase();
      const sign=({GBP:"£",USD:"$",EUR:"€",PLN:"zł",THB:"฿",AED:"د.إ",SGD:"S$",AUD:"A$",CAD:"C$",NZD:"NZ$",CHF:"CHF",CNY:"¥",HKD:"HK$",INR:"₹"})[currency]||currency;
      const delegated=Math.max(0,Number(form.arc_discretionary_budget_gbp||0));
      const allocated=ARC_DEPARTMENTS.reduce((sum,name)=>sum+Math.max(0,Number(form.department_budgets_gbp?.[name]||0)),0);
      const unallocated=Math.max(0,delegated-allocated);
      const allocationPct=delegated?Math.min(999,Math.round((allocated/delegated)*100)):0;
      const overAllocated=allocated>delegated;
      return <div className="setup-pane">
        <div className="setup-heading"><CircleDollarSign size={21}/><div><span>03 · Capital constitution</span><h2>Delegate capital like a board, not a checkbox.</h2><p>ARC compiles these owner limits into proposed department operating mandates. Nothing here grants real-world authority by itself.</p></div></div>
        <div className="money-grid">
          {[
            ["monthly_revenue_gbp","Monthly revenue","Current company baseline"],
            ["cash_gbp","Cash available","Treasury context"],
            ["monthly_operating_budget_gbp","Monthly operating budget","Company operating envelope"],
            ["minimum_cash_buffer_gbp","Minimum cash buffer","Protected owner liquidity floor"],
            ["arc_discretionary_budget_gbp","ARC delegated budget","Maximum monthly discretionary envelope"],
            ["single_action_limit_gbp","Single action limit","Hard ceiling before owner escalation"],
          ].map(([key,label,note])=><label className="money-field" key={key}><span>{label}</span><div><i>{sign}</i><input inputMode="decimal" value={form[key]} onChange={e=>setForm({[key]:e.target.value})}/></div><small>{note}</small></label>)}
        </div>
        <div className="capital-allocation-shell">
          <div className="capital-allocation-head">
            <div><span>Department operating mandates</span><b>Allocate the delegated envelope across the executive organism.</b></div>
            <div className={cls("allocation-meter",overAllocated&&"danger")}><b>{allocationPct}%</b><span>{overAllocated?"over delegated limit":`${sign} ${unallocated.toLocaleString()} unallocated`}</span></div>
          </div>
          <div className="department-budget-grid">
            {ARC_DEPARTMENTS.map(name=><label className="department-budget" key={name}>
              <span>{name}</span>
              <div><i>{sign}</i><input inputMode="decimal" value={form.department_budgets_gbp?.[name]||""} onChange={e=>setForm({department_budgets_gbp:{...(form.department_budgets_gbp||{}),[name]:e.target.value}})}/></div>
              <small>{name==="finance"?"reporting only until finance write authority exists":name==="sales"?"CRM + customer communication envelope":name==="marketing"?"ads, social and content envelope":name==="customer"?"support communication + CRM envelope":name==="operations"?"coordination + simulation envelope":"observe/simulate first; new powers fail closed"}</small>
            </label>)}
          </div>
          <div className={cls("capital-guardrail",overAllocated&&"danger")}>
            <ShieldCheck size={15}/><div><b>{overAllocated?"Allocation exceeds owner envelope":"Constitutional capital guardrail"}</b><span>{overAllocated?"Reduce department budgets before this stage can be saved.":"Each department will receive at most its own budget and the global single-action ceiling; unsupported powers remain observe-only."}</span></div>
          </div>
        </div>
      </div>;
    }

    if(step==="autonomy")return <div className="setup-pane">
      <div className="setup-heading"><BrainCircuit size={21}/><div><span>04 · Autonomy</span><h2>Choose the CEO operating mode.</h2><p>The preset is only the starting constitution. Every department/action can later be narrowed independently.</p></div></div>
      <div className="autonomy-choice-grid">
        {Object.entries(modeDetails).map(([id,mode])=><button key={id} className={cls("autonomy-choice",form.preset===id&&"selected")} onClick={()=>setForm({preset:id})}>
          <div className="autonomy-choice-top"><span>{id==="FULL"?<Zap size={18}/>:id==="MEDIUM"?<Activity size={18}/>:<ShieldCheck size={18}/>}</span><Pill tone={id==="FULL"?"violet":id==="MEDIUM"?"cyan":"green"}>{mode.tag}</Pill></div>
          <h3>{mode.label}</h3><p>{mode.copy}</p>
          <div className="autonomy-bullets">{mode.bullets.map(x=><span key={x}><CheckCircle2 size={12}/>{x}</span>)}</div>
        </button>)}
      </div>
      <div className="autonomy-warning"><ShieldCheck size={17}/><div><b>FULL does not mean unlimited.</b><span>Action Firewall, budget limits, prohibited actions and human-reserved legal/capital decisions remain hard boundaries.</span></div></div>
    </div>;

    if(step==="integrations"){
      const configured=state.integrations||{};
      const admission=ventureHandoff?.draft?.admission||null;
      const requiredIntegrations=new Set(admission?.required_integrations||[]);
      const groups=[...new Set(catalog.map(x=>x.group))];
      return <div className="setup-pane connections-pane">
        <div className="setup-heading"><PlugZap size={21}/><div><span>05 · Company nervous system</span><h2>Connect the systems ARC must understand and operate.</h2><p>Prefer OAuth and scoped access. Never give ARC your personal master passwords. Direct credentials are encrypted and never rendered back to the browser.</p></div></div>
        <div className={cls("vault-banner",state.vault_available?"ready":"locked")}>
          <LockKeyhole size={17}/><div><b>{state.vault_available?"Encrypted credential vault ready":"Credential vault locked"}</b>
          <span>{state.vault_available?"AES-GCM at-rest encryption is available for unavoidable API/app credentials.":"OAuth connection planning remains available, but direct secret storage fails closed until the operator configures ARC_PORTAL_VAULT_KEY."}</span></div>
        </div>
        {groups.map(group=><div className="integration-group" key={group}>
          <div className="integration-group-head"><b>{group}</b><span>{catalog.filter(x=>x.group===group).length} systems</span></div>
          <div className="integration-grid">{catalog.filter(x=>x.group===group).map(spec=>{
            const current=configured[spec.id];
            const status=current?.status||"not_connected";
            const ventureRequired=requiredIntegrations.has(spec.id);
            return <button className={cls("integration-card",status!=="not_connected"&&"configured",ventureRequired&&"venture-required")} key={spec.id} onClick={()=>setIntegration(spec)}>
              <div className="integration-card-top"><span className="integration-symbol">{spec.name.slice(0,2).toUpperCase()}</span><Pill tone={status==="configured"?"green":ventureRequired?"violet":status==="oauth_pending"?"amber":"neutral"}>{ventureRequired&&status==="not_connected"?"AVF required":status.replaceAll("_"," ")}</Pill></div>
              <b>{spec.name}</b><p>{spec.description}</p>
              <div className="cap-row">{(spec.capabilities||[]).slice(0,3).map(cap=><span key={cap}>{cap}</span>)}</div>
              {ventureRequired&&<small className="venture-required-note"><Orbit size={10}/> required by imported venture evidence contract</small>}
              {current?.has_secret&&<small><LockKeyhole size={10}/> encrypted credential stored</small>}
            </button>;
          })}</div>
        </div>)}
      </div>;
    }

    if(step==="constitution")return <div className="setup-pane">
      <div className="setup-heading"><ShieldCheck size={21}/><div><span>06 · Owner Constitution</span><h2>Write the boundaries ARC cannot negotiate away.</h2><p>This is the owner authority layer: legal, financial and strategic decisions that remain reserved even under FULL CEO.</p></div></div>
      <div className="constitution-grid">
        <div className="reserved-list"><span>Reserved owner decisions</span>
          {[
            ["change_company_ownership","Ownership / control"],
            ["borrow_or_issue_equity","Debt or equity issuance"],
            ["sign_material_legal_commitment","Material legal commitments"],
            ["hire_or_terminate_executive","Executive hiring / termination"],
            ["move_money_above_owner_limit","Capital above delegated limit"],
            ["change_constitution","Change this Constitution"],
            ["grant_new_external_authority","Grant new external authority"],
          ].map(([id,label])=><label className="check-row" key={id}>
            <input type="checkbox" checked={(form.reserved_decisions||[]).includes(id)}
              onChange={e=>setForm({reserved_decisions:e.target.checked?[...(form.reserved_decisions||[]),id]:(form.reserved_decisions||[]).filter(x=>x!==id)})}/>
            <span>{label}</span>
          </label>)}
        </div>
        <div className="form-grid">
          <label><span>Risk posture</span><select value={form.risk_tolerance} onChange={e=>setForm({risk_tolerance:e.target.value})}><option value="conservative">Conservative</option><option value="balanced">Balanced</option><option value="growth">Growth-oriented</option></select></label>
          <label><span>Prohibited actions · one per line</span><textarea value={form.prohibited_actions} onChange={e=>setForm({prohibited_actions:e.target.value})} placeholder={"Never delete customer data\nNever enter crypto positions"}/></label>
          <label><span>Brand / communication rules</span><textarea value={form.brand_rules} onChange={e=>setForm({brand_rules:e.target.value})} placeholder="Tone, promises ARC must never make, protected reputation rules…"/></label>
          <label><span>Legal / compliance notes</span><textarea value={form.legal_notes} onChange={e=>setForm({legal_notes:e.target.value})} placeholder="Contractual, regulatory or jurisdiction-specific restrictions…"/></label>
        </div>
      </div>
    </div>;

    if(step==="briefing")return <div className="setup-pane">
      <div className="setup-heading"><Activity size={21}/><div><span>07 · Executive rhythm</span><h2>Choose how ARC briefs you every morning.</h2><p>One concise executive brief replaces a stream of operational noise. Exceptions still escalate immediately when policy requires it.</p></div></div>
      <div className="brief-preview">
        <div className="brief-config">
          <label><span>Delivery hour</span><input type="number" min="0" max="23" value={form.hour} onChange={e=>setForm({hour:Number(e.target.value)})}/></label>
          <label><span>Minute</span><input type="number" min="0" max="59" value={form.minute} onChange={e=>setForm({minute:Number(e.target.value)})}/></label>
          <label><span>Primary channel</span><select value={form.channel} onChange={e=>setForm({channel:e.target.value})}><option value="portal">ARC portal</option><option value="email">Email</option><option value="slack">Slack / Teams</option></select></label>
          <label className="check-row"><input type="checkbox" checked={form.include_weekends} onChange={e=>setForm({include_weekends:e.target.checked})}/><span>Include weekends</span></label>
        </div>
        <div className="morning-brief-card">
          <div className="brief-card-head"><div><span>07:30 · EXECUTIVE MORNING BRIEF</span><b>Good morning. The company is operating normally.</b></div><StatusDot tone="green"/></div>
          <div className="brief-block"><span>Since yesterday</span><b>12 actions completed · 2 experiments advanced · 0 authority breaches</b></div>
          <div className="brief-block"><span>Money</span><b>Cash protected · forecast inside owner envelope</b></div>
          <div className="brief-block"><span>ARC is doing today</span><b>Pipeline follow-ups · campaign iteration · supplier review</b></div>
          <div className="brief-block attention"><span>Needs you</span><b>1 reserved decision · material contract approval</b></div>
        </div>
      </div>
    </div>;

    const connectedCount=Object.values(state.integrations||{}).filter(x=>["configured","connected","read_verified"].includes(x.status)).length;
    const ventureAdmission=ventureHandoff?.draft?.admission||null;
    return <div className="setup-pane">
      <div className="setup-heading"><Zap size={21}/><div><span>08 · Review & activation</span><h2>ARC should understand the company before it acts.</h2><p>Activation starts a company-model analysis. It does not silently switch on production writes.</p></div></div>
      <div className="readiness-layout">
        <div className="readiness-score"><div className="score-ring" style={{"--score":String(state.readiness?.score||0)+"%"}}><b>{state.readiness?.score||0}</b><span>readiness</span></div>
          <div><b>{state.readiness?.analysis_ready?"Analysis can begin":"More company context required"}</b><span>{connectedCount} company systems configured</span></div>
        </div>
        <div className="review-summary">
          <div><span>Company</span><b>{forms.company.name||account.user.company_name}</b></div>
          <div><span>CEO mode</span><b>{forms.autonomy.preset}</b></div>
          <div><span>ARC budget</span><b>{forms.company?.currency||"GBP"} {num(forms.finance.arc_discretionary_budget_gbp||0,0)}/mo</b></div>
          <div><span>Brief</span><b>{String(forms.briefing.hour).padStart(2,"0")}:{String(forms.briefing.minute).padStart(2,"0")} · {forms.briefing.channel}</b></div>
        </div>
      </div>
      {ventureAdmission&&<div className="venture-admission">
        <div className="venture-admission-head">
          <div><span>AVF → ARC admission corridor</span><b>{ventureAdmission.venture_id} · {ventureAdmission.world_class}</b><small>{ventureAdmission.admission_state?.replaceAll("_"," ")}</small></div>
          <div><Pill tone="violet">{ventureAdmission.evidence?.evidence_class||"evidence pending"}</Pill><code>{ventureAdmission.handoff_sha256?.slice(0,16)}…</code></div>
        </div>
        <div className="admission-evidence-grid">
          <div><span>Empirical</span><b>{ventureAdmission.evidence?.observed_empirical_count||0}</b><small>observed / validated records</small></div>
          <div><span>Simulation</span><b>{ventureAdmission.evidence?.simulation_evidence_count||0}</b><small>evidence packets</small></div>
          <div><span>Causal</span><b>{ventureAdmission.evidence?.causal_evidence_count||0}</b><small>explicit epistemic claims</small></div>
          <div><span>Authority</span><b>{ventureAdmission.authority_granted?"GRANTED":"NOT GRANTED"}</b><small>AVF cannot assign production authority</small></div>
        </div>
        <div className="autonomy-frontier">
          {Object.entries(ventureAdmission.autonomy_frontier||{}).map(([preset,row])=><div key={preset}>
            <span>{preset} shadow</span>
            <b>{row.shadow?.allowed||0}/{row.shadow?.total_intents||0} policy-clear</b>
            <small>{row.shadow?.needs_owner_approval||0} owner · {row.shadow?.denied||0} blocked · {row.coverage?.missing_rule_capabilities?.length||0} rule gaps</small>
          </div>)}
        </div>
        <div className="admission-integrations">
          <span>Required company nervous system</span>
          <div>{(ventureAdmission.required_integrations||[]).map(id=><i key={id}>{id.replaceAll("_"," ")}</i>)}</div>
        </div>
        <div className="supplemental-evidence">
          <div className="supplemental-evidence-head">
            <div><span>Supplemental AVF experiments</span><b>{ventureEvidence.length} verified paired model {ventureEvidence.length===1?"experiment":"experiments"}</b></div>
            <small>model evidence only · never an authority upgrade</small>
          </div>
          {ventureEvidencePortfolio&&<div className="evidence-portfolio">
            <div className="evidence-portfolio-grid">
              <div><span>Model support</span><b>{ventureEvidencePortfolio.model_support_band?.replaceAll("_"," ")}</b><small>{ventureEvidencePortfolio.empirical_status?.replaceAll("_"," ")}</small></div>
              <div><span>Breadth</span><b>{ventureEvidencePortfolio.coverage?.breadth||0}/{ventureEvidencePortfolio.coverage?.total_dimensions||6}</b><small>model-risk dimensions covered</small></div>
              <div><span>Branches</span><b>{ventureEvidencePortfolio.unique_branches||0}</b><small>{ventureEvidencePortfolio.unique_child_runs||0} child worlds</small></div>
              <div className={(ventureEvidencePortfolio.contradictions||[]).length?"danger":""}><span>Contradictions</span><b>{(ventureEvidencePortfolio.contradictions||[]).length}</b><small>{(ventureEvidencePortfolio.contradictions||[]).length?"requires replication":"no sign conflicts observed"}</small></div>
            </div>
            <div className="evidence-coverage-line">
              <span>covered</span>
              <b>{(ventureEvidencePortfolio.coverage?.covered_dimensions||[]).join(" · ")||"none"}</b>
            </div>
            {(ventureEvidencePortfolio.coverage?.missing_dimensions||[]).length>0&&<div className="evidence-coverage-line missing">
              <span>missing</span>
              <b>{ventureEvidencePortfolio.coverage.missing_dimensions.join(" · ")}</b>
            </div>}
            <div className="portfolio-truth"><ShieldCheck size={11}/><span>{ventureEvidencePortfolio.truth_note}</span></div>
          </div>}
          <div className="evidence-pull">
            <div>
              <span>Import a completed Observatory fork</span>
              <small>ARC fetches only from its operator-trusted AVF endpoint and independently verifies the packet.</small>
            </div>
            <input value={evidenceJobId} onChange={e=>setEvidenceJobId(e.target.value.trim())} placeholder="fork_3a9b79ad1ec18cf943065d22"/>
            <button onClick={pullVentureEvidence} disabled={evidenceBusy||!evidenceJobId}>{evidenceBusy?"Verifying…":"Import evidence"}</button>
          </div>
          {ventureEvidence.length===0?<div className="supplemental-empty">No live AVF experiment evidence has been admitted for this venture yet.</div>:
            <div className="supplemental-evidence-list">{ventureEvidence.slice(0,5).map(item=><div className="supplemental-evidence-card" key={item.sha256}>
              <div className="supplemental-evidence-card-head">
                <div><span>{item.intervention?.id?.replaceAll("-"," ")}</span><b>{item.epistemic_status?.replaceAll("_"," ")}</b></div>
                <code>{item.sha256?.slice(0,12)}…</code>
              </div>
              <div className="lineage-row">
                <span>parent <code>{item.parent_run_id?.slice(0,10)}…</code></span>
                <ChevronRight size={10}/>
                <span>child <code>{item.child_run_id?.slice(0,10)}…</code></span>
                <i>M{Number(item.first_divergence_month??-1)+1}</i>
              </div>
              <div className="evidence-delta-row">
                <span>cash Δ <b>£{num((item.delta?.terminal_cash_pence||0)/100,0)}</b></span>
                <span>revenue Δ <b>£{num((item.delta?.revenue_pence||0)/100,0)}</b></span>
                <span>backlog Δ <b>{num(item.delta?.terminal_backlog_units||0,0)}</b></span>
              </div>
            </div>)}</div>}
        </div>
        <div className="analysis-authority"><ShieldCheck size={13}/><span>{ventureAdmission.truth_note}</span></div>
      </div>}
      {(state.readiness?.missing||[]).length>0&&<div className="readiness-list"><b>Still required</b>{state.readiness.missing.map(x=><span key={x}><X size={11}/>{x}</span>)}</div>}
      {(state.readiness?.warnings||[]).length>0&&<div className="readiness-list warnings"><b>Coverage warnings</b>{state.readiness.warnings.map(x=><span key={x}><Activity size={11}/>{x}</span>)}</div>}
      <div className="review-checks">
        <label className="check-row"><input type="checkbox" checked={form.confirmed} onChange={e=>setForm({confirmed:e.target.checked})}/><span>I confirm the company information and objectives are accurate.</span></label>
        <label className="check-row"><input type="checkbox" checked={form.owner_acknowledged_authority} onChange={e=>setForm({owner_acknowledged_authority:e.target.checked})}/><span>I understand autonomy is bounded by the Constitution, budgets and Action Firewall.</span></label>
        <label className="check-row"><input type="checkbox" checked={form.owner_acknowledged_secrets} onChange={e=>setForm({owner_acknowledged_secrets:e.target.checked})}/><span>I used scoped/OAuth credentials rather than personal master passwords wherever possible.</span></label>
      </div>
      {state.activation_state==="analysis_pending"&&<div className="activation-success"><BrainCircuit size={20}/><div><b>Company intelligence build requested.</b><span>ARC will ingest connected reality, map the ontology, establish baselines and produce an owner review before real-world authority can activate.</span></div></div>}
      {activationAnalysis&&<div className="activation-analysis">
        <div className="activation-analysis-head">
          <div><span>Pre-ingestion intelligence</span><b>{activationAnalysis.next_gate?.replaceAll("_"," ")}</b></div>
          <code>{activationHash?activationHash.slice(0,18)+"…":"manifest pending"}</code>
        </div>
        <div className="coverage-grid">
          {Object.entries(activationAnalysis.coverage||{}).map(([domain,coverage])=><div className={cls("coverage-card",coverage.status)} key={domain}>
            <span>{domain}</span><b>{coverage.status}</b><small>{(coverage.matched_capabilities||[]).join(" · ")||"no verified capability"}</small>
          </div>)}
        </div>
        {activationAnalysis.capital_preflight&&<div className="capital-preflight-grid">
          <div><span>Owner-protected cash</span><b>£{num(activationAnalysis.capital_preflight.minimum_cash_buffer_pence/100||0,0)}</b><small>minimum liquidity floor</small></div>
          <div><span>Delegated to ARC</span><b>£{num(activationAnalysis.capital_preflight.arc_discretionary_budget_pence/100||0,0)}</b><small>monthly discretionary ceiling</small></div>
          <div><span>Department allocated</span><b>£{num(activationAnalysis.capital_preflight.department_allocated_pence/100||0,0)}</b><small>explicit mandate ownership</small></div>
          <div className={activationAnalysis.capital_preflight.buffer_survives_full_delegated_spend===false?"danger":""}><span>Stress buffer</span><b>{activationAnalysis.capital_preflight.buffer_survives_full_delegated_spend===false?"FAIL":"PASS"}</b><small>after full delegated spend</small></div>
        </div>}
        {(activationAnalysis.warnings||[]).length>0&&<div className="analysis-warnings">
          {(activationAnalysis.warnings||[]).map((item,i)=><div key={i}><Pill tone={item.severity==="blocking"?"red":"amber"}>{item.severity}</Pill><span>{item.detail}</span></div>)}
        </div>}
        <div className="ingestion-order">
          <b>Planned truth-ingestion order</b>
          {(activationAnalysis.ingestion_order||[]).map((item,i)=><span key={item.domain}><i>{String(i+1).padStart(2,"0")}</i><b>{item.domain}</b><small>{item.integrations.join(", ")}</small></span>)}
        </div>
        <div className="analysis-authority"><ShieldCheck size={13}/><span>{activationAnalysis.truth_note} · authority: {activationAnalysis.real_world_authority}</span></div>
      </div>}
    </div>;
  };

  return <div className="setup-shell">
    <aside className="setup-sidebar">
      <button className="setup-logo" onClick={onExit}><div className="brand-mark"><Orbit size={20}/></div><div><b>ARC</b><span>Company activation</span></div></button>
      <div className="setup-company"><span>workspace</span><b>{account.user.company_name}</b><small>{account.user.email}</small></div>
      <div className="setup-progress"><div><span>Readiness</span><b>{state.readiness?.score||0}%</b></div><i><em style={{width:String(state.readiness?.score||0)+"%"}}/></i></div>
      <nav><button className={cls("handover-nav-entry",showHandoverGuide&&"active")} onClick={()=>setShowHandoverGuide(true)}><span><Sparkles size={14}/></span><div><small>START</small><b>Handover guide</b></div></button>{ONBOARDING_STEPS.map(([id,label,Icon],index)=><button key={id} className={cls(!showHandoverGuide&&step===id&&"active",index<stepIndex&&"visited")} onClick={()=>{setShowHandoverGuide(false);setStep(id);}}>
        <span>{index<stepIndex?<CheckCircle2 size={14}/>:<Icon size={14}/>}</span><div><small>{String(index+1).padStart(2,"0")}</small><b>{label}</b></div>
      </button>)}</nav>
      <div className="setup-sidebar-foot"><ShieldCheck size={14}/><span>Configuration changes are owner actions. Credentials never appear in the ledger.</span></div>
    </aside>

    <main className="setup-main">
      <header className="setup-topbar">
        <div><span>ARC Global · Customer activation</span><b>{showHandoverGuide?"Your delegation roadmap":ONBOARDING_STEPS[stepIndex][1]}</b></div>
        <div className="setup-topbar-actions">
          {!showHandoverGuide&&<button className="public-link setup-back-guide" onClick={()=>setShowHandoverGuide(true)}><Sparkles size={14}/> Handover guide</button>}
          <CompanyRuntimeSwitcher account={account} compact onChanged={reloadActiveCompany}/>
          <Pill tone={state.vault_available?"green":"amber"}>{state.vault_available?"vault ready":"vault locked"}</Pill>
          <button className="public-link" onClick={onExit}>Exit setup</button>
        </div>
      </header>
      <div className="setup-content">
        {showHandoverGuide
          ?<HandoverGuide handover={handover} company={state.company}
              onOpenCEO={()=>onEnterApp("ceo")}
              onStep={id=>{setShowHandoverGuide(false);setStep(id);}}
              onTemplate={id=>applyHandoverTemplate(id)}
              onRefresh={()=>portalApi("/api/portal/handover-readiness").then(x=>setHandover(x.handover)).catch(e=>setError(String(e.message||e)))}/>
          :renderStep()}
        {error&&<div className="setup-error">{error}</div>}
        {!showHandoverGuide&&<div className="setup-actions">
          <button className="public-secondary" disabled={stepIndex===0||busy} onClick={prev}>Back</button>
          <div className="setup-actions-right">
            {step!=="integrations"&&step!=="review"&&<button className="public-link" disabled={busy} onClick={()=>save({advance:false})}>Save progress</button>}
            {step==="review"
              ?<button className="public-primary" disabled={busy||state.activation_state==="analysis_pending"} onClick={activate}>{busy?"Checking…":state.activation_state==="analysis_pending"?"Analysis requested":"Request ARC analysis"} <BrainCircuit size={14}/></button>
              :<button className="public-primary" disabled={busy} onClick={()=>save()}>{busy?"Saving…":"Continue"} <ChevronRight size={14}/></button>}
          </div>
        </div>}
      </div>
    </main>

    {integration&&<IntegrationModal spec={integration} current={state.integrations?.[integration.id]} csrf={account.csrf}
      onClose={()=>setIntegration(null)} onSaved={snapshot=>{setState(snapshot);hydrate(snapshot);}}/>}
  </div>;
}


function CEOOperations({account}) {
  const [state,setState]=useState(null);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [kind,setKind]=useState("booking_request");
  const [content,setContent]=useState("");
  const [feedback,setFeedback]=useState("");

  const reload=async()=> {
    if(!account?.authenticated)return;
    const payload=await portalApi("/api/portal/ceo/dashboard");
    setState(payload.ceo);
  };
  useEffect(()=>{setState(null);setError("");reload().catch(e=>setError(e.message));},
    [account?.authenticated,account?.user?.org_id]);
  const operate=async (path,data={})=>{
    setLoading(true);setError("");setFeedback("");
    try {
      await portalApi(path,{
        method:"POST",headers:{"X-CSRF-Token":account.csrf},
        body:JSON.stringify({...data,csrf:account.csrf})
      });
      await reload();
      setFeedback(path.endsWith("cycle")
        ?"CEO cycle recorded. Internal work was planned; no external action was executed."
        :path.endsWith("observe")
          ?"Signal recorded as owner-declared; run the cycle to triage."
          :"Human outcome recorded, with audit history preserved.");
    } catch(e) {setError(String(e.message||e).replaceAll("_"," "));}
    finally {setLoading(false);}
  };

  if(!account?.authenticated)return <div className="page-stack">
    <section className="ceo-hero"><div className="ceo-overline">ARC GLOBAL / CUSTOMER WORKSPACE</div>
      <h1>Your company's CEO Operating Room</h1>
      <p>The public product preview is not your live business. Create a company workspace to use a persisted, tenant-isolated operating loop.</p>
      <button className="primary" onClick={()=>{location.hash="onboarding";}}>Set up my company <ChevronRight size={15}/></button>
    </section>
  </div>;

  const counts=state?.counts||{};
  const tasks=state?.tasks||[];
  const attention=tasks.filter(x=>x.status==="awaiting_owner");
  const internal=tasks.filter(x=>x.status==="open");
  return <div className="page-stack ceo-page">
    <section className="ceo-hero">
      <div className="ceo-overline"><Orbit size={15}/> ARC GLOBAL / CEO OPERATING ROOM <span className="ceo-tag">COMPANY-SCOPED</span></div>
      <h1>Your company. One command center.</h1>
      <p>Capture evidence, run the executive coordination cycle, triage decisions, and track human-confirmed outcomes. Authority and financial execution remain separately commissioned.</p>
      <div className="ceo-actionbar">
        <button className="primary" onClick={()=>operate("/api/portal/ceo/cycle")} disabled={loading||!state}>
          <BrainCircuit size={16}/>{loading?"Processing…":"Run CEO operating cycle"}
        </button>
        <button className="ghost" onClick={()=>reload().catch(e=>setError(e.message))} disabled={loading}>Refresh evidence</button>
      </div>
      <div className="ceo-truth"><ShieldCheck size={16}/>
        <span><b>TRUTH GATE:</b> Internal coordination only · Owner reports are unverified · No client messages, funds, bookings or external operations executed by this page</span>
      </div>
    </section>
    {error&&<div className="ceo-feedback error" role="alert">{error}</div>}
    {feedback&&<div className="ceo-feedback" role="status">{feedback}</div>}
    {!state?<Panel title="Loading company operating evidence">Loading company-scoped state…</Panel>:<>
      <div className="ceo-metrics">
        <div><span>Signals ingested</span><strong>{counts.observations||0}</strong><small>Owner-reported, not automatically verified</small></div>
        <div><span>Executive cycles</span><strong>{counts.cycles||0}</strong><small>Persisted and replay-safe</small></div>
        <div><span>Owner decisions</span><strong>{counts.owner_attention||0}</strong><small>Waiting for review</small></div>
        <div><span>Internal work</span><strong>{counts.internal_open||0}</strong><small>Coordination queue</small></div>
      </div>
      <Panel title="Autonomous executive rhythm" kicker="Per-company opt-in · internal coordination only">
        <div className="ceo-scheduler">
          <div><b>{state.worker?.enabled?"Scheduled coordination enabled":"Scheduled coordination disabled"}</b>
            <p>{state.worker?.enabled
              ?"This workspace has authorized a 60-minute internal CEO cycle. Processing requires a deployed ARC worker. External actions remain locked."
              :"Allow an ARC worker to process signals, create internal tasks and update your briefing every 60 minutes. No external actions or money movement."}</p>
          </div>
          <button className="ghost" disabled={loading} onClick={()=>operate("/api/portal/ceo/worker",{
            enabled:!state.worker?.enabled,interval_minutes:60,
          })}>{state.worker?.enabled?"Disable scheduler":"Allow internal scheduler"}</button>
        </div>
      </Panel>
      <div className="ceo-columns">
        <Panel title="Signal intake" kicker="Evidence first / every business / every country">
          <p className="ceo-help">What happened in your business? Record a real observation. ARC will classify it, create a follow-up, and preserve provenance.</p>
          <label className="ceo-label" htmlFor="ceo-kind">Signal type</label>
          <select id="ceo-kind" className="ceo-input" value={kind} onChange={e=>setKind(e.target.value)}>
            {(state.observation_kinds||[]).map(k=><option key={k} value={k}>{k.replaceAll("_"," ")}</option>)}
          </select>
          <label className="ceo-label" htmlFor="ceo-content">What occurred?</label>
          <textarea id="ceo-content" className="ceo-input ceo-textarea" value={content}
            maxLength={600} onChange={e=>setContent(e.target.value)}
            placeholder="E.g. A customer requested a sunset cruise; capacity not yet confirmed."/>
          <button className="primary" disabled={loading||!content.trim()}
            onClick={async()=>{await operate("/api/portal/ceo/observe",{kind,content,source:"owner_report"});setContent("");}}>
            Record signal
          </button>
        </Panel>
        <Panel title="Your executive briefing" kicker="Live tenant records / no synthetic company outcomes">
          <div className="ceo-brief"><div className="ceo-brief-date">{state.brief?.date} · {state.company?.name}</div>
            <h3>{state.brief?.headline}</h3>
            <p>Completed internal or human-reported outcomes: <b>{state.brief?.since_yesterday?.completed_actions||0}</b></p>
            <p>Capital status: <b>Not independently reconciled</b></p>
            <p>Autonomy preset requested: <b>{state.autonomy_requested}</b></p>
            <div className="ceo-brief-rule">ARC has not been commissioned for external financial or legal decisions. Unverified inputs are explicitly flagged.</div>
          </div>
        </Panel>
      </div>
      <div className="ceo-columns">
        <Panel title="Decisions reserved for people" kicker="Owner / manager review">
          {attention.length?attention.slice(0,12).map(t=><div className="ceo-work" key={t.task_id}>
            <div className="ceo-work-top"><Pill tone="amber">{t.severity}</Pill><span>{t.role}</span></div>
            <b>{t.title}</b><p>{t.description}</p><small>{t.provenance}</small>
            <button className="ghost" disabled={loading} onClick={()=>{
              const note=window.prompt("Record the outcome you actually performed. ARC will NOT perform it for you.");
              if(note?.trim())operate("/api/portal/ceo/task",{task_id:t.task_id,status:"completed",note});
            }}>Record outcome</button>
          </div>):<p className="ceo-help">No unresolved owner decisions in this company's recorded queue.</p>}
        </Panel>
        <Panel title="Autonomous coordination work" kicker="Internal actions · no external spend">
          {internal.length?internal.slice(0,12).map(t=><div className="ceo-work" key={t.task_id}>
            <div className="ceo-work-top"><Pill tone="neutral">{t.severity}</Pill><span>{t.role}</span></div>
            <b>{t.title}</b><p>{t.description}</p><small>{t.provenance}</small>
            <button className="ghost" disabled={loading} onClick={()=>{
              const note=window.prompt("What was completed? Give the actual observed result.");
              if(note?.trim())operate("/api/portal/ceo/task",{task_id:t.task_id,status:"completed",note});
            }}>Confirm result</button>
          </div>):<p className="ceo-help">No internal items currently queued. Record a signal or run a CEO cycle.</p>}
        </Panel>
      </div>
      <Panel title="Executive board challenge" kicker="Deterministic checks · no simulated AI opinions">
        <div className="ceo-board">
          {(state.executive_review?.assessments||[]).map(a=><div className="ceo-board-card" key={a.role}>
            <div><b>{a.role}</b><Pill tone={a.verdict==="VETO"||a.verdict==="HOLD"?"amber":"neutral"}>{a.verdict.replaceAll("_"," ")}</Pill></div>
            <p>{a.reason}</p><small>{a.source}</small>
          </div>)}
        </div>
        <p className="ceo-help">Risk veto: <strong>{state.executive_review?.veto_active?"ACTIVE":"No explicit hard veto in recorded evidence"}</strong>. These are transparent rule checks, not claims of multi-model reasoning. External authority is never granted from this report.</p>
      </Panel>
      <Panel title="Evidence trail" kicker="Observations belong only to the selected company">
        <div className="ceo-history">{(state.observations||[]).length?(state.observations||[]).slice(0,8).map(o=>
          <div className="ceo-history-row" key={o.observation_id}>
            <span>{o.kind.replaceAll("_"," ")}</span><b>{o.content}</b>
            <small>{o.evidence_class}</small>
          </div>):<p className="ceo-help">Nothing recorded yet. Data is not invented to fill this view.</p>}</div>
      </Panel>
    </>}
  </div>;
}

const HANDOVER_PREVIEW={
 setup_percent:0,setup_complete:0,setup_total:8,phase:"PREPARATION",verified_live_integrations:[],
 stages:[
  ["company","Company identity","Company name, country/market, industry, services, website, timezone and reporting currency."],
  ["objectives","CEO mission","Measurable goals, customer experience standards, what ARC must never do."],
  ["finance","Financial reality","Real revenue baseline, budget, protected cash reserve and limits on each action."],
  ["autonomy","Level of delegation","Choose LIGHT, MEDIUM or FULL and restrict particular departments."],
  ["integrations","Connected company systems","Company email, calendar, CRM, booking tools, Stripe, accounting and ads; prefer OAuth."],
  ["constitution","Reserved human authority","Protected decisions, safety, contracts, capital, refunds and emergency contacts."],
  ["briefing","Owner reports and escalation","Select time, timezone, daily reports and urgent alerts."],
  ["review","Owner sign-off","Inspect the full scope of what ARC knows and request shadow-mode verification."],
 ].map(([id,title,client_gives])=>({id,title,client_gives,complete:false}))
};

function HandoverPreview(){
 const [target,setTarget]=useState("");
 const [example,setExample]=useState("");
 return <div className="handover-preview-wrap">
   <div className="handover-preview-bar"><ShieldCheck size={16}/>
     <span><b>INTERACTIVE DESIGN PREVIEW</b> — no customer data, no account access, no real actions, no payments. Real onboarding will use your company's protected workspace.</span>
     <button onClick={()=>location.hash=""}>Back to ARC <X size={15}/></button>
   </div>
   <div className="handover-preview-shell">
     {target&&<div className="handover-demo-selection" role="status"><b>{target==="example"?("Example selected: "+example):("Setup step: "+target.replaceAll("_"," "))}</b>
       <span>In the connected customer workspace this opens the secure editable form. The public preview does not store credentials or company information.</span>
       <button onClick={()=>setTarget("")}>Close</button>
     </div>}
     <HandoverGuide handover={HANDOVER_PREVIEW} onStep={id=>{setTarget(id);window.scrollTo(0,0);}}
       onTemplate={id=>{setTarget("example");setExample(id==="siamluxe"?"Siam Luxe":"CamdenSiam");window.scrollTo(0,0);}}
       onRefresh={()=>setTarget("Preview only — open a secure customer workspace for current readiness")}/>
   </div>
 </div>;
}

function App(){
  const [publicData,setPublicData]=useState(null);
  const [data,setData]=useState(null);
  const [portalSession,setPortalSession]=useState(null);
  const [section,setSection]=useState("command");
  const [palette,setPalette]=useState(false);
  const [runtimeBusy,setRuntimeBusy]=useState(false);
  const routeFromHash=()=>location.hash==="#app"?"app":location.hash==="#onboarding"?"onboarding":location.hash==="#handover-preview"?"handover_preview":"public";
  const [route,setRoute]=useState(routeFromHash);

  const loadPublic=async()=>{
    const response=await fetch("./data/arc-snapshot.json",{cache:"no-store"});
    if(!response.ok)throw new Error("snapshot HTTP "+response.status);
    const snapshot=await response.json();
    setPublicData(snapshot);
    return snapshot;
  };

  const loadActiveRuntime=async()=>{
    setRuntimeBusy(true);
    try{
      const session=await portalApi("/api/portal/session");
      setPortalSession(session);
      if(!session.authenticated){
        if(publicData)setData(publicData);
        return null;
      }
      if(section==="ceo"){
        // Authenticated coordination dashboard runs without the production
        // runtime snapshot, which is gated until company onboarding completes.
        if(publicData)setData(publicData);
        return null;
      }
      try{
        const runtime=await portalApi("/api/portal/runtime-snapshot");
        setData(runtime.snapshot);
        return runtime.snapshot;
      }catch(err){
        if(err.status===409&&err.payload?.onboarding_required){
          if(publicData)setData(publicData);
          // The internal CEO planner is available as soon as real company
          // configuration begins. Other modules still require onboarding.
          if(section==="ceo")return null;
          location.hash="onboarding";
          setRoute("onboarding");
          return null;
        }
        throw err;
      }
    }finally{
      setRuntimeBusy(false);
    }
  };

  useEffect(()=>{
    loadPublic()
      .then(snapshot=>setData(snapshot))
      .catch(e=>setData({error:e.message}));
  },[]);

  useEffect(()=>{
    if(!publicData)return;
    if(route==="app"){
      loadActiveRuntime().catch(e=>setData({error:e.message}));
    }else{
      setData(publicData);
    }
  },[route,publicData]);

  useEffect(()=>{
    const key=e=>{
      if(route==="app"&&(e.metaKey||e.ctrlKey)&&e.key.toLowerCase()==="k"){e.preventDefault();setPalette(v=>!v);}
      if(e.key==="Escape")setPalette(false);
    };
    const hash=()=>setRoute(routeFromHash());
    addEventListener("keydown",key);addEventListener("hashchange",hash);
    return()=>{removeEventListener("keydown",key);removeEventListener("hashchange",hash);};
  },[route]);

  if(!data||runtimeBusy)return <div className="boot"><div className="boot-orb"><Orbit size={28}/></div><span>Waking the active company runtime…</span></div>;
  if(data.error)return <div className="boot error"><b>Control Centre failed closed</b><span>{data.error}</span></div>;
  if(route==="handover_preview")return <HandoverPreview/>;
  if(route==="public")return <PublicSite
    data={publicData||data}
    onEnterApp={()=>{location.hash="app";setRoute("app");}}
    onOnboarding={()=>{location.hash="onboarding";setRoute("onboarding");}}
  />;
  if(route==="onboarding")return <OnboardingExperience
    data={publicData||data}
    onEnterApp={target=>{if(target==="ceo")setSection("ceo");location.hash="app";setRoute("app");}}
    onExit={()=>{location.hash="";setRoute("public");}}
  />;

  const current=NAV.find(x=>x[0]===section)||NAV[0];
  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><div className="brand-mark"><Orbit size={21}/></div><div><b>ARC</b><span>Autonomous CEO</span></div></div>
      <div className="nav-label">Executive OS</div>
      <nav>{NAV.map(([id,label,Icon])=><button key={id} className={cls("nav-item",section===id&&"active")} onClick={()=>setSection(id)}>
        <Icon size={16}/><span>{label}</span>{section===id&&<i/>}</button>)}</nav>
      <div className="sidebar-space"/>
      <div className="organism-state">
        <div><StatusDot tone="green"/><span>Organism</span><b>{data.runtime?.state?"tenant":"demo"}</b></div>
        <div><StatusDot tone="cyan"/><span>Runtime</span><b className="mono">{data.runtime?.company_id||data.git_head||"demo"}</b></div>
        <div><StatusDot tone="violet"/><span>Mode</span><b>{data.authority?.preset}</b></div>
      </div>
      <button className="command-key" onClick={()=>setPalette(true)}><CommandIcon size={14}/><span>Command</span><kbd>⌘K</kbd></button>
    </aside>

    <main className="main-shell">
      <header className="topbar">
        <div><div className="crumb">ARC / {current[1]}</div><div className="top-state"><StatusDot/><span>{section==="ceo"&&portalSession?.authenticated
            ?`${portalSession?.user?.company_name||"Company workspace"} · INTERNAL_COORDINATION_ONLY`
            :`${data.company.name} · ${data.evidence_class}`}</span></div></div>
        <div className="top-actions">
          <CompanyRuntimeSwitcher account={portalSession} compact onChanged={loadActiveRuntime}/>
          <button className="search-btn" onClick={()=>setPalette(true)}><Search size={15}/><span>Search / command</span><kbd>⌘K</kbd></button>
          <div className="version"><span>runtime</span><b className="mono">{data.runtime?.company_id||data.git_head||"demo"}</b></div>
        </div>
      </header>
      <div className="content">
        {section==="command"&&<ExecutiveDeck data={data} setSection={setSection}/>}
        {section==="ceo"&&<CEOOperations account={portalSession}/>}
        {section==="organism"&&<Organism data={data}/>}
        {section==="strategy"&&<StrategyLab account={portalSession}/>}
        {section==="decisions"&&<Decisions data={data}/>}
        {section==="departments"&&<Departments data={data}/>}
        {section==="cognition"&&<Cognition data={data}/>}
        {section==="authority"&&<Authority data={data}/>}
        {section==="integrations"&&<Integrations data={data}/>}
      </div>
    </main>
    <Palette open={palette} setOpen={setPalette} setSection={setSection}/>
  </div>;
}

createRoot(document.getElementById("root")).render(<React.StrictMode><App/></React.StrictMode>);
